﻿import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { ArrowLeft, Plus, Target, Users, Zap, XCircle, Lock, Unlock, Printer, Radio, QrCodeIcon, Download, ChevronDown, Activity, FileText, Network, Eye, ShieldAlert, Menu, Settings, Trash2, LogOut, BookOpen } from 'lucide-react'
import IssueBounty from './IssueBounty'
import BountyBoard from './BountyBoard'
import AddPlayer from './AddPlayer'
import ViewSwitcher from './ViewSwitcher'
import { QRCode } from 'react-qr-code'
import EventMCS from './EventMCS'
import EquipmentLocations from './EquipmentLocations'
import WarCrimesTab from './WarCrimesTab'
import CallBlindManButton from './CallBlindManButton'
import NotificationBell from './NotificationBell'
import FragoBuilder from './FragoBuilder'
import FragoPhaseStepper from './FragoPhaseStepper'
import ShareQR from './ShareQR'
import QRDownloadCenter from './QRDownloadCenter'
import PlaySheet from './PlaySheet'
import { Calendar as CalendarIcon } from 'lucide-react'
import FactionBalanceWarning from './FactionBalanceWarning'
import { printQR, serializeSvgFromDom } from '../lib/printQR'
import useEventRole from '../lib/useEventRole'
import { isPlatformAdmin } from '../lib/roleHelpers'
import { canCreateContent } from '../lib/eventLifecycle'
import { eventStatusTag as statusTag } from '../lib/eventStatus'
import { isContentEditable, isSettingsEditable } from '../lib/eventPhase'
import { ChevronRight as ChevronRightIcon, Pencil, Check, X as XIcon } from 'lucide-react'
import { getEventFactionScores } from '../lib/db'
import { mutate, rpc } from '../lib/dbMutate'
import clearAllUserState from '../lib/authCleanup'

const C = {
  page: { minHeight: '100vh', background: '#000000', color: '#00ff66', fontFamily: "'JetBrains Mono', monospace", textShadow: '0 0 4px rgba(0,255,102,.5)', position: 'relative' },
  nav: { background: '#0a140a', borderBottom: '1px solid rgba(0,255,102,.18)', padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 10 },
  display: { fontFamily: "'VT323', 'Share Tech Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  dim: { color: '#4aaa5a' },
  deep: { color: '#2a6a3a' },
  panel: { background: 'rgba(0, 20, 8, .4)', border: '1px solid rgba(0,255,102,.18)', boxShadow: '0 0 6px rgba(0,255,102,.08)', position: 'relative' },
  panelBright: { background: 'rgba(0, 20, 8, .4)', border: '1px solid #00ff66', boxShadow: '0 0 12px rgba(0,255,102,.15), inset 0 0 20px rgba(0,255,102,.04)', position: 'relative' },
  select: { background: '#0a140a', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '4px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', outline: 'none' },
  btnPrimary: { fontFamily: "'VT323', monospace", fontSize: '13px', letterSpacing: '0.1em', textTransform: 'uppercase', background: '#00ff66', color: '#000000', border: 'none', padding: '6px 14px', cursor: 'pointer', boxShadow: '0 0 6px #00ff66', textShadow: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' },
  btnSecondary: { fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', background: 'rgba(0, 20, 8, .4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', padding: '6px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  btnDanger: { fontFamily: "'VT323', monospace", fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase', background: 'rgba(255,42,42,.15)', color: '#ff2a2a', border: '1px solid rgba(255,42,42,.3)', padding: '6px 14px', cursor: 'pointer', textShadow: '0 0 4px rgba(255,42,42,.3)' },
  btnStatus: (bg, color) => ({ fontFamily: "'VT323', monospace", fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase', background: bg, color, border: 'none', padding: '6px 12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }),
  tag: { fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 8px', textShadow: 'none' },
  // Track 6.2 — 44x44 min hit area on the GM tab bar. Was 8px×14px
  // padding on a 14px font ≈ 32px tall, under the WCAG spec.
  tab: (active) => ({ fontFamily: "'VT323', monospace", fontSize: '14px', letterSpacing: '0.08em', textTransform: 'uppercase', background: active ? '#00ff66' : 'rgba(0,20,8,.4)', color: active ? '#000' : '#4aaa5a', border: active ? 'none' : '1px solid rgba(0,255,102,.18)', padding: '10px 16px', minHeight: '44px', minWidth: '44px', cursor: 'pointer', textShadow: active ? 'none' : '0 0 4px rgba(0,255,102,.3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', boxShadow: active ? '0 0 6px #00ff66' : 'none' }),
}


// State-machine forward transitions for the "next step" nav button.
// destructive=true triggers a confirm prompt before the RPC fires.
const NEXT_STEP = {
  draft:        { target: 'prep',         label: 'TO PREP',        destructive: false, icon: 'next' },
  prep:         { target: 'registration', label: 'OPEN REGISTRATION', destructive: false, icon: 'next' },
  registration: { target: 'locked',       label: 'LOCK ROSTER',    destructive: true,  icon: 'lock' },
  locked:       { target: 'live',         label: 'GO LIVE',        destructive: true,  icon: 'live' },
  live:         { target: 'endex',        label: 'CALL ENDEX',     destructive: true,  icon: 'stop' },
  endex:        { target: 'closed',       label: 'CLOSE EVENT',    destructive: true,  icon: 'close' },
}

const roleTag = {
  gm: { background: 'rgba(154,102,255,.12)', color: '#b380ff', border: '1px solid rgba(154,102,255,.3)' },
  co_gm: { background: 'rgba(77,160,255,.08)', color: '#4da0ff', border: '1px solid rgba(77,160,255,.3)' },
  delegate: { background: 'rgba(0,200,200,.08)', color: '#00cccc', border: '1px solid rgba(0,200,200,.3)' },
  // ANU-90 — In-Game Admin chip. Orange so it reads as "referee" at a
  // glance, distinct from the purple/blue GM tiers.
  iga: { background: 'rgba(255,140,40,.10)', color: '#ff9a3d', border: '1px solid rgba(255,140,40,.35)' },
}

function Brackets({ children, style = {}, className = '', bright = false }) {
  const ps = bright ? C.panelBright : C.panel
  const color = bright ? '#9aff9a' : '#4aaa5a'
  return (
    <div className={className} style={{ ...ps, ...style }}>
      <div style={{ position:'absolute',top:-1,left:-1,width:8,height:8,borderTop:`2px solid ${color}`,borderLeft:`2px solid ${color}` }}/>
      <div style={{ position:'absolute',bottom:-1,right:-1,width:8,height:8,borderBottom:`2px solid ${color}`,borderRight:`2px solid ${color}` }}/>
      {children}
    </div>
  )
}

// ANU-39: ISO timestamp → "YYYY-MM-DDTHH:mm" in local time, the format
// <input type="datetime-local"> expects. toISOString() would give UTC
// which the input would silently shift by the local offset.
function isoToLocalDatetimeInput(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fmtScheduleStamp(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// ANU-39: defined at module scope so the React reference is stable
// between GameEvent re-renders. If this lived inline inside the parent
// the datetime-local inputs would unmount and lose focus on every
// keystroke (same anti-pattern fixed in ANU-36 and the FragoForm note
// above).
function EventScheduleStrip({ event, canEdit, onSave }) {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState({ starts_at: '', ends_at: '' })

  const startEdit = () => {
    setDraft({
      starts_at: isoToLocalDatetimeInput(event.starts_at),
      ends_at: isoToLocalDatetimeInput(event.ends_at),
    })
    setEditing(true)
  }
  const save = async () => {
    if (draft.starts_at && draft.ends_at && new Date(draft.ends_at) <= new Date(draft.starts_at)) {
      alert('End must be after start.')
      return
    }
    setSaving(true)
    const ok = await onSave({
      starts_at: draft.starts_at ? new Date(draft.starts_at).toISOString() : null,
      ends_at:   draft.ends_at   ? new Date(draft.ends_at).toISOString()   : null,
    })
    setSaving(false)
    if (ok) setEditing(false)
  }

  const inputStyle = {
    background: 'rgba(0,20,8,.6)', color: '#00ff66',
    border: '1px solid rgba(0,255,102,.25)',
    padding: '4px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
    outline: 'none',
  }

  if (editing) {
    return (
      <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 4 }}>
        <CalendarIcon size={11} style={{ color: '#4aaa5a' }} />
        <input type="datetime-local" value={draft.starts_at}
          onChange={e => setDraft(d => ({ ...d, starts_at: e.target.value }))}
          style={inputStyle} aria-label="Event start" />
        <span style={{ color: '#2a6a3a', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>→</span>
        <input type="datetime-local" value={draft.ends_at}
          onChange={e => setDraft(d => ({ ...d, ends_at: e.target.value }))}
          style={inputStyle} aria-label="Event end" />
        <button onClick={save} disabled={saving}
          style={{ background: '#00ff66', color: '#000', border: 'none', padding: '4px 10px', cursor: saving ? 'wait' : 'pointer', fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {saving ? '...' : 'save'}
        </button>
        <button onClick={() => setEditing(false)}
          style={{ background: 'rgba(0,20,8,.4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', padding: '4px 8px', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
          cancel
        </button>
      </div>
    )
  }

  const startStamp = fmtScheduleStamp(event.starts_at)
  const endStamp = fmtScheduleStamp(event.ends_at)
  if (!startStamp && !endStamp) {
    if (!canEdit) return null
    return (
      <button onClick={startEdit}
        style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer', padding: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
        <CalendarIcon size={11} /> + set schedule
      </button>
    )
  }

  return (
    <div className="flex items-center gap-1" style={{ marginTop: 4, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#4aaa5a', minWidth: 0, overflow: 'hidden' }}>
      <CalendarIcon size={11} style={{ flexShrink: 0 }} />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
        <span style={{ whiteSpace: 'nowrap' }}>{startStamp || '—'} <span style={{ color: '#2a6a3a' }}>→</span></span>
        <span style={{ whiteSpace: 'nowrap' }}>{endStamp || '—'}</span>
      </div>
      {canEdit && (
        <button onClick={startEdit} title="Edit schedule"
          style={{ background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer', padding: 0, display: 'inline-flex', flexShrink: 0 }}>
          <Pencil size={11} />
        </button>
      )}
    </div>
  )
}

export default function GameEvent({ event, user, onBack }) {
  const [players, setPlayers] = useState([])
  const [bounties, setBounties] = useState([])
  const [showIssueBounty, setShowIssueBounty] = useState(false)
  const [showAddPlayer, setShowAddPlayer] = useState(false)
  const [activeTab, setActiveTab] = useState('sitrep')
  const [collapsedSections, setCollapsedSections] = useState({})
  // ANU-89 — pending war-crime count for the tab badge. Realtime sub so a
  // new INSERT bumps the badge immediately without waiting for a poll.
  const [warCrimesPending, setWarCrimesPending] = useState(0)
  // ANU-91 — BLIND MAN safety state. is_paused / who called / when /
  // reason. The GM banner + resume button reads from this; the full
  // overlay is NOT mounted in Command Center because the GM needs to
  // see and interact with the resume control.
  const [blindMan, setBlindMan] = useState({ isPaused: false, calledAt: null, reason: null, callerCallsign: null })
  const [blindManElapsed, setBlindManElapsed] = useState(0)
  const [resumeBusy, setResumeBusy] = useState(false)
  const [eventData, setEventData] = useState(event)
  // Roster is "locked" once registration closes — i.e. anything past the
  // registration phase. Used to gate roster-edit UI.
  const isLocked = ['locked','live','endex','closed'].includes(eventData.status)
  const [activePause, setActivePause] = useState(null)
  const [navMenuOpen, setNavMenuOpen] = useState(false)
  const [factions, setFactions] = useState([])
  const [delegateScopes, setDelegateScopes] = useState([])
  const [showShareQR, setShowShareQR] = useState(false)
  const [showBulkQR, setShowBulkQR] = useState(false)
  const [fragoAssignments, setFragoAssignments] = useState([])
  // FRAGO Rework Phase 2: per-FRAGO phase + objective counts pulled from
  // the new frago_phases / frago_objectives tables. Map keyed by FRAGO
  // (bounty) id so FragoCard renders without an N+1 round-trip per card.
  const [fragoStructure, setFragoStructure] = useState({})
  const [recentActivity, setRecentActivity] = useState([])
  const [mcsNodes, setMcsNodes] = useState([])
  const [showFragoForm, setShowFragoForm] = useState(false)
  // ANU-206 — edit-in-place state. null when creating, populated when editing.
  const [editingFragoData, setEditingFragoData] = useState(null)
  // UX-1 — FRAGO card expand + inline edit state. Keyed by bounty id.
  const [expandedFrago, setExpandedFrago] = useState({})
  const [editingFragoId, setEditingFragoId] = useState(null)
  const [fragoDraft, setFragoDraft] = useState({ mission_name: '', points: '', notes: '' })
  // useEventRole is the single source of truth for what this viewer can see.
  // isGm stays for read-only legacy call sites; new gating uses isFullGM (no
  // m55-admin pseudo-GM ambiguity) + canViewFaction(faction_id).
  // NOTE: this block must come AFTER every useState it references — a temporal
  // dead-zone read of mcsNodes/fragoAssignments here will crash on first render.
  const eventRole = useEventRole(event.id)
  const { isFullGM, isCoGm, coGmFactionId, canViewFaction } = eventRole
  const isGm = isFullGM || isCoGm

  // Faction-aware view slices. Full-GM gets the full set; co-GMs get only
  // the slice scoped to their faction. Built once per render and handed off
  // to children — no need to repeat the predicate at every call site.
  const visibleFactions = isCoGm
    ? (factions || []).filter(f => f.id === coGmFactionId)
    : (factions || [])
  const playerFactionMap = {}
  ;(players || []).forEach(p => { if (p) playerFactionMap[p.user_id] = p.faction_id })
  // Type-aware co-GM visibility. Per Dax's gameplay design:
  //   FRAGO broadcast      → target_faction_id (the broadcast audience)
  //   FRAGO delegate/multi → any assigned node in this faction
  //   Kill                 → pursuing faction only (target's faction !==
  //                          viewer's). Target gets a push banner; target's
  //                          team sees nothing on the board. Fog of war —
  //                          if the target wants the team to know, they
  //                          radio it.
  //   Defend / escort / …  → target's faction (protect-side scoped)
  const nodeFactionMap = {}
  ;(mcsNodes || []).forEach(n => { if (n) nodeFactionMap[n.id] = n.faction_id })
  const bountyInvolvesFaction = (b, factionId) => {
    if (!b || !factionId) return false
    if (b.target_faction_id) return b.target_faction_id === factionId
    // ANU-41 — broadcast-to-all FRAGOs (target_type='faction' with
    // NULL target_faction_id) are visible to every faction's co-GM,
    // mirroring the player-side MISSIONS rule.
    if (b.category === 'frago' && b.target_type === 'faction' && b.target_faction_id == null) return true
    if (b.category === 'frago') {
      const myAssigns = (fragoAssignments || []).filter(a => a && a.bounty_id === b.id)
      return myAssigns.some(a => nodeFactionMap[a.node_id] === factionId)
    }
    const targetFaction = b.target_id ? playerFactionMap[b.target_id] : null
    if (!targetFaction) return false
    if (b.bounty_type === 'kill') return targetFaction !== factionId
    return targetFaction === factionId
  }
  const visibleBounties = isCoGm
    ? (bounties || []).filter(b => bountyInvolvesFaction(b, coGmFactionId))
    : (bounties || [])
  const visiblePlayers = isCoGm
    ? (players || []).filter(p => p && (!p.faction_id || p.faction_id === coGmFactionId))
    : (players || [])
  const visibleMcsNodes = isCoGm
    ? (mcsNodes || []).filter(n => n && (!n.faction_id || n.faction_id === coGmFactionId))
    : (mcsNodes || [])
  // Final score-hiding signal: org has the flag AND the viewer isn't a
  // full GM. Co-GMs are scoped but still see scores (they need the
  // operational picture for their faction); the spec calls this out as
  // GM-only override.
  // Org features (read-only). Currently used for play_sheet_mode and the
  // hide_faction_scores / aar_faction_reveal toggles. Pulled once.
  const [orgFeatures, setOrgFeatures] = useState({})
  const [orgFieldIntelEnabled, setOrgFieldIntelEnabled] = useState(true)

  const hideScoresForViewer = !!orgFeatures.hide_faction_scores && !isFullGM
  const [bountyStatusFilter, setBountyStatusFilter] = useState('active')
  const [fragoStatusFilter, setFragoStatusFilter] = useState('active')
  const [fragoSubmitting, setFragoSubmitting] = useState(false)
  // Intel + scheduled-deployment surfaces for SITREP WARNO + commander card.
  const [nextDeployment, setNextDeployment] = useState(null)
  const [pendingIntel, setPendingIntel] = useState([])
  // Equipment list — drives the FragoForm objective-picker.
  const [equipment, setEquipment] = useState([])
  // Faction scoreboard — aggregated from v_player_event_stats. Refreshed
  // alongside players so it tracks roster changes + bounty claims.
  const [factionScores, setFactionScores] = useState([])
  // Checkpoints for SITREP CP card. Polled by the same tick that drives
  // the deployment engine. cp_visibility (org setting) gates render.
  const [checkpoints, setCheckpoints] = useState([])
  const [scheduledBreaks, setScheduledBreaks] = useState([])
  const [showAddBreak, setShowAddBreak] = useState(false)
  const [breakDraft, setBreakDraft] = useState({ name: '', start: '', end: '' })
  const [settingsScheduleDraft, setSettingsScheduleDraft] = useState({ starts_at: '', ends_at: '' })
  const [settingsScheduleEditing, setSettingsScheduleEditing] = useState(false)
  const [settingsScheduleSaving, setSettingsScheduleSaving] = useState(false)
  // ANU-201 — DOCTRINE tab: template library + DEPLOY modal.
  const [doctrineTemplates, setDoctrineTemplates] = useState(null)
  const [deployModal, setDeployModal] = useState(null)
  const [deployForm, setDeployForm] = useState({})
  const [deploySubmitting, setDeploySubmitting] = useState(false)

  // Score-hiding takes effect for non-full-GMs only. Full GMs always see
  // the full picture so they can run the event; co-GMs and any
  // m55-pseudo-GM that lands here get the same hidden treatment as
  // players when the org has the flag on. The actual computation is
  // deferred until isFullGM is in scope (later in this function body).

  useEffect(() => {
    fetchPlayers(); fetchBounties(); fetchFactions(); fetchDelegateScopes(); fetchFragoAssignments(); fetchFragoStructure(); fetchMcsNodes(); fetchActivePause(); fetchScheduledBreaks(); fetchIntelAndDeployments(); fetchEquipment(); fetchFactionScores(); fetchOrgFeatures(); fetchCheckpoints()
    // Return cleanup so StrictMode (and unmount) tears the realtime
    // subscription + 30s expire_bounties interval down — otherwise these
    // stack up and amplify every refetch.
    return subscribeToChanges()
  }, [])

  // Re-pull faction scores whenever bounties change (claims update points).
  useEffect(() => { fetchFactionScores() }, [bounties])

  // ANU-201 — load doctrine templates lazily on first DOCTRINE tab open.
  useEffect(() => {
    if (activeTab !== 'doctrine' || doctrineTemplates !== null) return
    fetchDoctrineTemplates()
  }, [activeTab])

  // ANU-91 — BLIND MAN state for the GM banner + resume control.
  // Subscribes to events.is_paused. Pulls the caller's callsign on
  // every flip so the banner can label who triggered the ceasefire.
  useEffect(() => {
    if (!eventData?.id) return
    let cancelled = false
    const load = async () => {
      const { data } = await supabase
        .from('events')
        .select('is_paused, blind_man_called_at, blind_man_reason, blind_man_called_by')
        .eq('id', eventData.id).limit(1)
      if (cancelled) return
      const ev = data?.[0]
      if (!ev) return
      let callsign = null
      if (ev.blind_man_called_by) {
        const { data: u } = await supabase
          .from('users').select('callsign, display_name')
          .eq('id', ev.blind_man_called_by).limit(1)
        callsign = u?.[0]?.callsign || u?.[0]?.display_name || null
      }
      if (cancelled) return
      setBlindMan({
        isPaused: !!ev.is_paused,
        calledAt: ev.blind_man_called_at || null,
        reason: ev.blind_man_reason || null,
        callerCallsign: callsign,
      })
    }
    load()
    const channel = supabase
      .channel(`gm-blindman-${eventData.id}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'events', filter: `id=eq.${eventData.id}` },
        () => load())
      .subscribe()
    return () => { cancelled = true; try { supabase.removeChannel(channel) } catch { /* ignore */ } }
  }, [eventData?.id])

  // Live duration counter for the BLIND MAN banner — ticks while paused.
  useEffect(() => {
    if (!blindMan.isPaused || !blindMan.calledAt) { setBlindManElapsed(0); return }
    const tick = () => setBlindManElapsed(Math.max(0, Math.floor((Date.now() - new Date(blindMan.calledAt).getTime()) / 1000)))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [blindMan.isPaused, blindMan.calledAt])

  const resumeBlindMan = async () => {
    if (!confirm('Lift BLIND MAN ceasefire and resume play?')) return
    setResumeBusy(true)
    const { error } = await supabase.rpc('resume_from_blind_man', { p_event_id: eventData.id })
    setResumeBusy(false)
    if (error) {
      alert('Failed to resume: ' + error.message)
    }
  }

  // ANU-89 — pending war-crime count for the tab badge. Realtime sub so a
  // brand-new INSERT bumps the badge before the GM tab-clicks anything.
  useEffect(() => {
    if (!eventData?.id) return
    let cancelled = false
    const load = async () => {
      const { count } = await supabase
        .from('war_crimes')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventData.id)
        .eq('status', 'pending')
      if (!cancelled) setWarCrimesPending(count || 0)
    }
    load()
    const channel = supabase
      .channel(`gm-warcrimes-${eventData.id}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'war_crimes', filter: `event_id=eq.${eventData.id}` },
        () => load())
      .subscribe()
    return () => { cancelled = true; try { supabase.removeChannel(channel) } catch { /* ignore */ } }
  }, [eventData?.id])

  const fetchFactionScores = async () => {
    // v_player_event_stats has total_points + faction_id per (user, event);
    // getEventFactionScores rolls those rows up into one entry per faction.
    const data = await getEventFactionScores(event.id)
    setFactionScores(data || [])
  }

  // Pull organizations.features once; play_sheet_mode + the fog-of-war
  // toggles are GM-controlled org-wide. Failing back to defaults keeps the
  // UI working even if the org row is missing.
  const fetchOrgFeatures = async () => {
    if (!event?.org_id) return
    const { data } = await supabase.from('organizations').select('features, field_intel_enabled').eq('id', event.org_id).limit(1)
    setOrgFeatures(data?.[0]?.features || {})
    setOrgFieldIntelEnabled(data?.[0]?.field_intel_enabled ?? true)
  }

  // ANU-201 — Fetch frago + bounty templates for the event's org (including platform templates).
  const fetchDoctrineTemplates = async () => {
    if (!event?.org_id) return
    const [{ data: fragoTpls }, { data: bountyTpls }] = await Promise.all([
      supabase.from('frago_templates').select('*').or(`org_id.eq.${event.org_id},is_platform_template.eq.true`),
      supabase.from('bounty_templates').select('*').or(`org_id.eq.${event.org_id},is_platform_template.eq.true`),
    ])
    setDoctrineTemplates({ frago: fragoTpls || [], bounty: bountyTpls || [] })
  }

  // ANU-206 — build the initialValues object for FragoBuilder edit mode.
  // Maps DB schema (bounty + frago_phases + frago_objectives) → builder state format.
  const buildFragoInitialValues = ({ bounty, phases, objectives }) => {
    const parseRes = (r) => r ? {
      activate_bounty_id: r.activate_bounty_id || '',
      trigger_frago_id: r.trigger_frago_id || '',
      message: r.message || '',
      cancel_bounty_id: r.cancel_bounty_ids?.[0] || '',
      cancel_frago_id:  r.cancel_frago_ids?.[0]  || '',
      fail_bounty_id:   r.fail_bounty_ids?.[0]   || '',
      fail_frago_id:    r.fail_frago_ids?.[0]    || '',
    } : null
    return {
      title:               bounty.mission_name || '',
      description:         bounty.frago_description || '',
      overallTimeout:      bounty.overall_timeout_minutes ?? '',
      endScoreOverride:    bounty.end_score_override ?? '',
      triggerType:         bounty.activation_trigger_type || 'manual',
      triggerConfig:       bounty.activation_trigger_config || {},
      activationMessage:   bounty.activation_message || '',
      commanderNotify:     bounty.commander_notify ?? true,
      commanderMessage:    bounty.commander_message || '',
      targetFactionId:     bounty.target_faction_id || '',
      opposingNotifyEnabled: !!bounty.opposing_notify_on_trigger,
      opposingNotifyMessage: bounty.opposing_notify_on_trigger?.message || '',
      scenarioRoles:       bounty.scenario_roles || [],
      onSuccess:           parseRes(bounty.resolution_on_success),
      onFail:              parseRes(bounty.resolution_on_fail),
      phases: phases.map(ph => ({
        title:            ph.title || '',
        briefing:         ph.briefing || '',
        assignmentType:   ph.assignment_type || 'broadcast',
        assignmentNodeId: ph.assignment_node_id || '',
        phaseScore:       ph.phase_score ?? '',
        objectives: objectives
          .filter(o => o.phase_id === ph.id)
          .sort((a, b) => a.objective_number - b.objective_number)
          .map(o => ({
            title:               o.title || '',
            type:                o.objective_type || 'manual',
            target_id:           o.target_id || '',
            duration_minutes:    o.duration_minutes ?? '',
            destination_id:      o.destination_id || '',
            concurrent:          o.concurrent ?? false,
            is_fail_condition:   o.is_fail_condition ?? false,
            fail_faction_scope:  o.fail_faction_scope || 'opposing',
            notify_enabled:      !!o.notify_config,
            notify_who:          o.notify_config?.who || 'commander',
            notify_message:      o.notify_config?.message || '',
            opposing_enabled:    !!o.opposing_effect,
            opposing_message:    o.opposing_effect?.message || '',
            opposing_trigger_frago_id:    o.opposing_effect?.trigger_frago_id || '',
            opposing_activate_bounty_id:  o.opposing_effect?.activate_bounty_id || '',
          }))
      }))
    }
  }

  // ANU-206 — open FragoBuilder in edit mode: fetch existing phases + objectives,
  // build initialValues, show the builder pre-populated.
  const startFullFragoEdit = async (bounty) => {
    const { data: phases } = await supabase
      .from('frago_phases').select('*').eq('frago_id', bounty.id).order('phase_number')
    const phaseIds = (phases || []).map(p => p.id)
    const { data: objectives } = phaseIds.length
      ? await supabase.from('frago_objectives').select('*').in('phase_id', phaseIds).order('objective_number')
      : { data: [] }
    setEditingFragoData({ bounty, phases: phases || [], objectives: objectives || [] })
    setShowFragoForm(true)
  }

  // ANU-206 — save an in-place FRAGO edit: UPDATE bounty row, then delete
  // and re-insert phases + objectives from the builder output.
  const updateFragoStructured = async (f) => {
    if (fragoSubmitting || !editingFragoData) return
    setFragoSubmitting(true)
    const fragoId = editingFragoData.bounty.id

    const { error: bErr } = await supabase.from('bounties').update({
      mission_name:               f.title,
      frago_description:          f.description,
      notes:                      f.activation_message,
      activation_trigger_type:    f.activation_trigger_type,
      activation_trigger_config:  f.activation_trigger_config,
      activation_message:         f.activation_message,
      commander_notify:           f.commander_notify,
      commander_message:          f.commander_message,
      scenario_roles:             (Array.isArray(f.scenario_roles) && f.scenario_roles.length > 0) ? f.scenario_roles : null,
      target_faction_id:          f.target_faction_id ?? null,
      opposing_notify_on_trigger: f.opposing_notify_on_trigger ?? null,
      end_score_override:         f.end_score_override ?? null,
      resolution_on_success:      f.resolution_on_success,
      resolution_on_fail:         f.resolution_on_fail,
      overall_timeout_minutes:    f.overall_timeout_minutes,
      doctrine_reviewed:          true,
    }).eq('id', fragoId).eq('status', 'queued')
    if (bErr) { alert('Edit failed: ' + bErr.message); setFragoSubmitting(false); return }

    // Delete existing phases (frago_objectives cascades via FK).
    await supabase.from('frago_objectives').delete().in(
      'phase_id',
      (await supabase.from('frago_phases').select('id').eq('frago_id', fragoId)).data?.map(p => p.id) || []
    )
    await supabase.from('frago_phases').delete().eq('frago_id', fragoId)

    // Re-insert phases + objectives from builder output.
    const phaseInserts = f.phases.map((ph, idx) => ({
      frago_id:           fragoId,
      phase_number:       idx + 1,
      title:              ph.title,
      briefing:           ph.briefing,
      assignment_type:    ph.assignment_type,
      assignment_node_id: ph.assignment_node_id,
      phase_score:        ph.phase_score ?? 0,
      status:             'locked',
    }))
    const { data: phaseRows, error: pErr } = await supabase
      .from('frago_phases').insert(phaseInserts).select('id, phase_number')
    if (pErr || !phaseRows) {
      alert('Phase re-insert failed: ' + (pErr?.message || 'unknown'))
      setFragoSubmitting(false); fetchBounties(); fetchFragoStructure(); return
    }
    phaseRows.sort((a, b) => a.phase_number - b.phase_number)
    for (let i = 1; i < phaseRows.length; i++) {
      await supabase.from('frago_phases').update({ gate_phase_id: phaseRows[i - 1].id }).eq('id', phaseRows[i].id)
    }
    const objInserts = []
    for (let i = 0; i < f.phases.length; i++) {
      const phId = phaseRows[i]?.id
      if (!phId) continue
      for (const obj of (f.phases[i].objectives || [])) {
        objInserts.push({
          phase_id:          phId,
          objective_number:  objInserts.filter(o => o.phase_id === phId).length + 1,
          title:             obj.title,
          objective_type:    obj.type,
          target_id:         obj.target_id || null,
          duration_minutes:  obj.duration_minutes || null,
          destination_id:    obj.destination_id || null,
          concurrent:        !!obj.concurrent,
          notify_config:     obj.notify_config || null,
          opposing_effect:   obj.opposing_effect || null,
          is_fail_condition: !!obj.is_fail_condition,
          fail_faction_scope: obj.is_fail_condition ? (obj.fail_faction_scope || 'opposing') : null,
        })
      }
    }
    if (objInserts.length) {
      const { error: oErr } = await supabase.from('frago_objectives').insert(objInserts)
      if (oErr) console.error('[FRAGO EDIT] objectives insert failed:', oErr)
    }

    fetchBounties(); fetchFragoStructure()
    setEditingFragoData(null)
    setShowFragoForm(false)
    setFragoSubmitting(false)
  }

  // ANU-201 — Instantiate a FRAGO template into a real queued FRAGO.
  const deployFragoFromTemplate = async () => {
    if (deploySubmitting || !deployModal) return
    setDeploySubmitting(true)
    const t = deployModal.template
    const { data: bountyRows, error: bErr } = await supabase.from('bounties').insert({
      event_id: event.id,
      issued_by: user.id,
      bounty_type: 'general_frago',
      category: 'frago',
      status: 'queued',
      phase: 'setup',
      points: 0,
      mission_name: deployForm.title || t.name,
      frago_description: t.description || '',
      target_type: 'faction',
      target_faction_id: deployForm.target_faction_id || null,
      activation_trigger_type: 'manual',
      activation_trigger_config: null,
      issued_at: new Date().toISOString(),
      source_frago_template_id: t.id,
    }).select('id').limit(1)
    if (bErr || !bountyRows?.[0]) {
      alert('DEPLOY failed: ' + (bErr?.message || 'unknown'))
      setDeploySubmitting(false); return
    }
    const fragoId = bountyRows[0].id
    // One phase with the template's objectives mapped to frago_objectives.
    const objs = Array.isArray(t.objectives) ? t.objectives : []
    const { data: phaseRow, error: pErr } = await supabase.from('frago_phases').insert({
      frago_id: fragoId,
      phase_number: 1,
      title: deployForm.title || t.name,
      briefing: t.description || '',
      assignment_type: 'broadcast',
      phase_score: 0,
      status: 'locked',
    }).select('id').limit(1)
    if (pErr) console.error('[DOCTRINE DEPLOY] phase insert failed:', pErr)
    if (phaseRow?.[0] && objs.length) {
      const { error: oErr } = await supabase.from('frago_objectives').insert(
        objs.map((o, idx) => ({
          phase_id: phaseRow[0].id,
          objective_number: idx + 1,
          title: o.title || `Objective ${idx + 1}`,
          objective_type: o.scan_type || 'manual',
          is_fail_condition: o.is_fail_condition ?? false,
          fail_faction_scope: o.fail_faction_scope || 'opposing',
        }))
      )
      if (oErr) console.error('[DOCTRINE DEPLOY] objectives insert failed:', oErr)
    }
    fetchBounties(); fetchFragoStructure()
    setDeployModal(null); setDeployForm({}); setDeploySubmitting(false)
    setActiveTab('fragos')
  }

  // ANU-201 — Instantiate a bounty template into a real active bounty.
  const deployBountyFromTemplate = async () => {
    if (deploySubmitting || !deployModal) return
    if (!deployForm.target_id) { alert('Select a target player.'); return }
    setDeploySubmitting(true)
    const t = deployModal.template
    const { error: bErr } = await supabase.from('bounties').insert({
      event_id: event.id,
      issued_by: user.id,
      bounty_type: t.target_type || 'general',
      category: 'bounty',
      status: 'active',
      phase: 'active',
      points: 0,
      mission_name: deployForm.title || t.title || t.name,
      notes: t.description || '',
      target_id: deployForm.target_id,
      duration_minutes: deployForm.duration ?? t.default_duration ?? 0,
      issued_at: new Date().toISOString(),
      source_template_id: t.id,
    })
    if (bErr) {
      alert('DEPLOY failed: ' + bErr.message)
      setDeploySubmitting(false); return
    }
    fetchBounties()
    setDeployModal(null); setDeployForm({}); setDeploySubmitting(false)
    setActiveTab('bounties')
  }

  // Checkpoints + their controlling/capturing faction tags.
  const fetchCheckpoints = async () => {
    const { data } = await supabase
      .from('event_checkpoints')
      .select('*, controlling_faction:event_factions!event_checkpoints_controlling_faction_id_fkey(id, name, color), capturing_faction:event_factions!event_checkpoints_capturing_faction_id_fkey(id, name, color)')
      .eq('event_id', event.id)
      .order('created_at', { ascending: false })
    setCheckpoints(data || [])
  }

  // ANU-125: processDueDeployments removed from client-side tick — scheduled_deployments
  // processing must be server-side (pg_cron). tick_checkpoints is a legitimate
  // client-driven RPC (checkpoint state machine requires a viewer to tick it).
  useEffect(() => {
    if (eventData.status !== 'live') return
    const tick = async () => {
      if (!navigator.onLine) return
      if (activePause) return
      try {
        await supabase.rpc('tick_checkpoints', { p_event_id: event.id })
        await fetchCheckpoints()
      } catch (err) {
        console.warn('[GameEvent] tick_checkpoints failed', err)
      }
    }
    tick()
    const interval = setInterval(tick, 30000)
    return () => clearInterval(interval)
  }, [eventData.status, activePause?.id])

  // Recent activity depends on which bounties exist — refresh whenever the
  // bounties list changes so newly-claimed kills + mission_events surface.
  useEffect(() => { fetchRecentActivity() }, [bounties])

  const fetchPlayers = async () => { const { data } = await supabase.from('event_players').select('*, users(id, display_name, email, photo_url, callsign)').eq('event_id', event.id); setPlayers(data || []) }
  const fetchBounties = async () => { const { data } = await supabase.from('bounties').select('*, target:users!bounties_target_id_fkey(callsign, display_name, email), issuer:users!bounties_issued_by_fkey(callsign, display_name, email), claimer:users!bounties_claimed_by_fkey(callsign, display_name, email)').eq('event_id', event.id).order('issued_at', { ascending: false }); setBounties(data || []) }
  const fetchFactions = async () => { const { data } = await supabase.from('event_factions').select('*').eq('event_id', event.id); setFactions(data || []) }
  const fetchDelegateScopes = async () => { const { data } = await supabase.from('delegate_scopes').select('*').eq('event_id', event.id); setDelegateScopes(data || []) }
  const fetchFragoAssignments = async () => {
    // Pull assignments for any frago bounty in this event. Two-step query
    // because frago_assignments doesn't carry event_id directly.
    const { data: fragoBounties } = await supabase.from('bounties').select('id').eq('event_id', event.id).eq('category', 'frago')
    if (!fragoBounties?.length) { setFragoAssignments([]); return }
    // Join the assigned team via mcs_nodes.assigned_team_id so the FragoCard
    // can render a team callsign next to the node name (Feature 6 — GM view
    // surfaces who actually owns a delegated FRAGO).
    const { data } = await supabase
      .from('frago_assignments')
      .select('*, node:mcs_nodes(id, name, callsign, faction_id, assigned_player_id, assigned_team_id, team:teams(id, name)), bounty:bounties(id, mission_name, points, status, notes), assigner:users!frago_assignments_assigned_by_fkey(callsign, display_name)')
      .in('bounty_id', fragoBounties.map(b => b.id))
      .order('assigned_at', { ascending: false })
    setFragoAssignments(data || [])
  }

  // Pull phase + objective counts for every FRAGO in this event in two
  // round-trips, then collapse into a {fragoId: {phases, objectives}} map.
  // Empty rows are normal for FRAGOs created before the rework (or via the
  // legacy form) — they show "no phases yet" in FragoCard.
  const fetchFragoStructure = async () => {
    const { data: fragoBounties } = await supabase.from('bounties')
      .select('id').eq('event_id', event.id).eq('category', 'frago')
    const fragoIds = (fragoBounties || []).map(b => b.id)
    if (fragoIds.length === 0) { setFragoStructure({}); return }
    const { data: phases } = await supabase.from('frago_phases')
      .select('id, frago_id').in('frago_id', fragoIds)
    const phaseIds = (phases || []).map(p => p.id)
    let objectives = []
    if (phaseIds.length > 0) {
      const { data: objs } = await supabase.from('frago_objectives')
        .select('id, phase_id').in('phase_id', phaseIds)
      objectives = objs || []
    }
    const phaseToFrago = Object.fromEntries((phases || []).map(p => [p.id, p.frago_id]))
    const map = {}
    fragoIds.forEach(id => { map[id] = { phases: 0, objectives: 0 } })
    ;(phases || []).forEach(p => { map[p.frago_id].phases += 1 })
    objectives.forEach(o => {
      const fid = phaseToFrago[o.phase_id]
      if (fid && map[fid]) map[fid].objectives += 1
    })
    setFragoStructure(map)
  }

  const fetchMcsNodes = async () => {
    const { data } = await supabase
      .from('mcs_nodes')
      .select('id, name, callsign, faction_id, parent_id, rank_order, assigned_player_id')
      .eq('event_id', event.id)
      .order('rank_order')
    setMcsNodes(data || [])
  }

  // === FRAGO ACTIONS ===

  // Phase 3 of the FRAGO Rework — structured save path used by FragoBuilder.
  // Writes the FRAGO across four tables in sequence:
  //   1. bounties           one row, status='queued', carries activation
  //                         trigger config + resolution JSONB
  //   2. frago_phases       one row per phase, with gate_phase_id chained
  //                         to the previous phase so the auto-completion
  //                         engine (Phase 4) only unlocks Phase N+1 when
  //                         Phase N flips to completed
  //   3. frago_objectives   one row per objective per phase
  //   4. frago_trigger_qr   one row IFF the activation trigger is
  //                         qr_discovery — code is the canonical
  //                         ANUBIS-FRAGO-{frago_id} form so the QR router
  //                         can reverse-lookup
  // Each insert checks for errors and bails loudly with an alert rather
  // than silently leaving a half-formed FRAGO.
  const createFragoStructured = async (f) => {
    if (fragoSubmitting) return
    setFragoSubmitting(true)

    // Step 1 — bounty row (the FRAGO header).
    const bountyData = {
      event_id: event.id,
      issued_by: user.id,
      bounty_type: 'general_frago',
      category: 'frago',
      // Status starts queued; the activation trigger (manual / scheduled /
      // QR / scan / kill / chain / phase) flips it to active when it fires.
      status: 'queued',
      phase: 'setup',
      points: f.points,
      mission_name: f.title,
      // FRAGO restructure — top-level narrative goes into the new
      // dedicated column. notes stays as a side channel for GM
      // operational comments (cancel reason, etc.) so the two don't
      // collide.
      frago_description: f.description,
      notes: f.activation_message,
      target_id: null,
      issued_at: new Date().toISOString(),
      activation_trigger_type: f.activation_trigger_type,
      activation_trigger_config: f.activation_trigger_config,
      activation_message: f.activation_message,
      commander_notify: f.commander_notify,
      commander_message: f.commander_message,
      // SEC 3 — routing strategy + direct-route node.
      commander_route: f.commander_route ?? 'commander',
      commander_route_node_id: f.commander_route_node_id ?? null,
      // SEC 4 — scenario role assignments (JSONB array).
      scenario_roles: (Array.isArray(f.scenario_roles) && f.scenario_roles.length > 0)
        ? f.scenario_roles
        : null,
      // ANU-41 — structured-builder FRAGOs are broadcast-audience by
      // design (per-phase assignment_type is independent of the
      // FRAGO's audience). target_faction_id NULL == event-wide
      // broadcast; specific id == scoped to that faction. target_type
      // pinned to 'faction' so the MISSIONS filter knows to honor it.
      target_type: 'faction',
      target_faction_id: f.target_faction_id ?? null,
      // FRAGO restructure — opposing-team alert + GM score override.
      opposing_notify_on_trigger: f.opposing_notify_on_trigger ?? null,
      end_score_override: f.end_score_override ?? null,
      resolution_on_success: f.resolution_on_success,
      resolution_on_fail:    f.resolution_on_fail,
      // resolution_on_timeout dropped from the new builder — timeout
      // collapses into the fail outcome.
      overall_timeout_minutes: f.overall_timeout_minutes,
    }
    // Track 1.2 — FRAGO save spans bounties + frago_phases + frago_objectives
    // (+ frago_trigger_qr when qr_discovery). All four are in TABLE_CACHE_MAP,
    // so wrapping the bounty insert is enough — the subsequent inserts run
    // inside this same code path and the cache flush at the end covers
    // them. Each child insert is left as a raw supabase call to preserve
    // the existing error-handling shape (alert + bail).
    const bountyResult = await mutate(
      ['bounties', 'frago_phases', 'frago_objectives', 'frago_trigger_qr'],
      () => supabase.from('bounties').insert(bountyData).select('id').limit(1)
    )
    const { data: bountyRows, error: bErr } = bountyResult
    if (bErr || !bountyRows?.[0]) {
      alert('Failed to create FRAGO: ' + (bountyResult.friendlyMessage || bErr?.message || 'unknown'))
      setFragoSubmitting(false); return
    }
    const fragoId = bountyRows[0].id

    // Step 2 — phases, in two passes. First insert all rows so we know
    // their ids; then update gate_phase_id on phases 2..N to point at the
    // prior phase's id. (Single-pass works for sequential gates but the
    // two-pass shape stays robust if we ever support fan-out gates.)
    const phaseInserts = f.phases.map((ph, idx) => ({
      frago_id: fragoId,
      phase_number: idx + 1,
      title: ph.title,
      briefing: ph.briefing,
      assignment_type: ph.assignment_type,
      assignment_node_id: ph.assignment_node_id,
      // FRAGO restructure — per-phase points feed the auto-tally
      // total when phases complete.
      phase_score: ph.phase_score ?? 0,
      // First phase is unlocked at activation; the rest start locked and
      // get flipped when their gate phase completes.
      status: idx === 0 ? 'locked' : 'locked',
    }))
    const { data: phaseRows, error: pErr } = await supabase
      .from('frago_phases').insert(phaseInserts).select('id, phase_number')
    if (pErr || !phaseRows) {
      alert('FRAGO created but phase insert failed: ' + (pErr?.message || 'unknown'))
      setFragoSubmitting(false); fetchBounties(); fetchFragoStructure(); return
    }
    // phaseRows order isn't guaranteed; sort by phase_number to chain gates.
    phaseRows.sort((a, b) => a.phase_number - b.phase_number)
    for (let i = 1; i < phaseRows.length; i++) {
      const { error: gErr } = await supabase
        .from('frago_phases')
        .update({ gate_phase_id: phaseRows[i - 1].id })
        .eq('id', phaseRows[i].id)
      if (gErr) console.warn('[FRAGO] gate chain update failed', gErr)
    }

    // Step 3 — objectives, batched per phase.
    // ANU-82 — capture inserted IDs so linked-bounty triggers below can
    // resolve their index-based references to real UUIDs. objectiveIdMap
    // is [phaseIdx][objIdx] -> uuid.
    const objectiveIdMap = []
    for (let i = 0; i < f.phases.length; i++) {
      const phase = f.phases[i]
      const phaseId = phaseRows[i].id
      const objInserts = phase.objectives.map((o, idx) => ({
        phase_id: phaseId,
        objective_number: idx + 1,
        title: o.title,
        objective_type: o.type,
        target_id: o.target_id,
        duration_minutes: o.duration_minutes,
        destination_id: o.destination_id,
        concurrent: o.concurrent,
        // FRAGO restructure — per-objective notify-on-complete +
        // opposing-team effect. Cleaned to null by the builder when
        // toggled off so the engine can skip without checking shape.
        notify_config: o.notify_config ?? null,
        opposing_effect: o.opposing_effect ?? null,
        is_fail_condition: !!o.is_fail_condition,
        fail_faction_scope: o.fail_faction_scope ?? null,
        status: 'locked',
      }))
      objectiveIdMap[i] = []
      if (objInserts.length > 0) {
        const { data: objRows, error: oErr } = await supabase
          .from('frago_objectives')
          .insert(objInserts)
          .select('id, objective_number')
        if (oErr) console.warn('[FRAGO] objective insert failed for phase', phase.title, oErr)
        if (objRows) {
          for (const row of objRows) {
            objectiveIdMap[i][row.objective_number - 1] = row.id
          }
        }
      }
    }

    // ANU-82 — INSERT linked bounties spawned inline in the FRAGO builder.
    // parent_frago_id ties each child to this FRAGO (so the ANU-81 lock
    // hides manual cancel/activate). status='queued'; linked_trigger
    // metadata drives the cascade triggers on bounties / frago_phases /
    // frago_objectives that flip the row to active/cancelled at the
    // right moment.
    for (const lb of (f.linked_bounties || [])) {
      const phaseIdx = lb.activation?.phase_idx
      const objPhaseIdx = lb.activation?.objective_phase_idx
      const objIdx = lb.activation?.objective_idx
      const activate_phase_id = lb.activation?.mode === 'on_phase' && phaseIdx != null
        ? phaseRows[phaseIdx]?.id ?? null
        : null
      const activate_objective_id = lb.activation?.mode === 'on_objective' && objPhaseIdx != null && objIdx != null
        ? (objectiveIdMap[objPhaseIdx] || [])[objIdx] ?? null
        : null
      const cancelObjPhaseIdx = lb.cancel?.objective_phase_idx
      const cancelObjIdx = lb.cancel?.objective_idx
      const cancel_objective_id = lb.cancel?.mode === 'on_objective' && cancelObjPhaseIdx != null && cancelObjIdx != null
        ? (objectiveIdMap[cancelObjPhaseIdx] || [])[cancelObjIdx] ?? null
        : null
      const linked_trigger = {
        activate: lb.activation?.mode || 'with_frago',
        activate_phase_id,
        activate_objective_id,
        cancel: lb.cancel?.mode || 'never',
        cancel_objective_id,
      }
      const childBounty = {
        event_id: event.id,
        issued_by: user.id,
        bounty_type: lb.type,
        category: 'bounty',
        status: 'queued',
        phase: 'active',
        points: lb.points,
        title: lb.title || null,
        target_id: lb.target_id,
        target_type: 'player',
        issued_at: new Date().toISOString(),
        active_duration: lb.active_duration_minutes,
        parent_frago_id: fragoId,
        linked_trigger,
        phase_scoped: lb.phase_scoped ?? true,
        persist_post_frago: lb.persist_post_frago ?? false,
      }
      const { data: lbData, error: lbErr } = await supabase.from('bounties').insert(childBounty).select('id').single()
      if (lbErr) { console.warn('[FRAGO] linked bounty insert failed', lbErr, lb); continue }

      // ANU-171 — T-ID per linked bounty
      if (lb.tid && lbData?.id) {
        const { error: lbTidErr } = await supabase.from('dossier_entries').insert({
          event_id:           event.id,
          parent_type:        'bounty',
          parent_id:          lbData.id,
          entry_type:         't_id',
          author_id:          user.id,
          body:               lb.tid.description?.trim() || lb.tid.callsign?.trim() || '',
          target_meta: {
            callsign:     lb.tid.callsign?.trim(),
            display_name: lb.tid.displayName?.trim() || null,
            description:  lb.tid.description?.trim(),
            last_known:   lb.tid.lastKnown?.trim() || null,
          },
          photo_storage_path: lb.tid.photoStoragePath || null,
        })
        if (lbTidErr) console.warn('[T-ID] linked bounty dossier insert failed', lbTidErr)
      }
    }

    // ANU-81 — stamp parent_frago_id on any child bounties this FRAGO's
    // resolution config will flip on success / fail. Locks the child rows
    // from manual cancel / activate in the UI; lifecycle now flows only
    // through the parent FRAGO's resolution path.
    const childBountyIds = [
      f.resolution_on_success?.activate_bounty_id,
      f.resolution_on_fail?.activate_bounty_id,
    ].filter(Boolean)
    if (childBountyIds.length > 0) {
      const { error: linkErr } = await supabase
        .from('bounties')
        .update({ parent_frago_id: fragoId })
        .in('id', childBountyIds)
      if (linkErr) console.warn('[FRAGO] parent_frago_id link failed', linkErr)
    }

    // Step 4 — generate the trigger QR if this is a qr_discovery FRAGO.
    // The code embeds the FRAGO id so the QR router can hop straight to
    // the right bounty without a separate frago_trigger_qr lookup.
    if (f.activation_trigger_type === 'qr_discovery') {
      const code = 'ANUBIS-FRAGO-' + fragoId
      const { error: qErr } = await supabase.from('frago_trigger_qr').insert({
        frago_id: fragoId,
        event_id: event.id,
        qr_code: code,
      })
      if (qErr) console.warn('[FRAGO] trigger QR insert failed', qErr)
    }

    // Log the issue + close out. mission_events.bounty_id is NOT NULL so
    // we always have a row to anchor the audit trail.
    await supabase.from('mission_events').insert({
      bounty_id: fragoId,
      event_type: 'frago_issued',
      triggered_by: user.id,
      notes: `Structured FRAGO (${f.phases.length} phases, trigger=${f.activation_trigger_type})`,
    })

    // ANU-88 — T-ID dossier entry
    if (f.tid) {
      const { error: tidErr } = await supabase.from('dossier_entries').insert({
        event_id:           event.id,
        parent_type:        'frago',
        parent_id:          fragoId,
        entry_type:         't_id',
        author_id:          user.id,
        body:               f.tid.description?.trim() || f.tid.callsign?.trim() || '',
        target_meta: {
          callsign:     f.tid.callsign?.trim(),
          display_name: f.tid.displayName?.trim() || null,
          description:  f.tid.description?.trim(),
          last_known:   f.tid.lastKnown?.trim() || null,
        },
        photo_storage_path: f.tid.photoStoragePath || null,
      })
      if (tidErr) console.warn('[T-ID] dossier_entries insert failed', tidErr)
    }

    setShowFragoForm(false)
    setFragoSubmitting(false)
    fetchBounties(); fetchFragoAssignments(); fetchFragoStructure()
  }

  // Resolve points to a list of user IDs to receive the bounty's points.
  // Broadcast → all event_players in the targeted faction.
  // Delegate/Multi → mcs_node_members of each assigned node + each node's
  // assigned_player_id (the leader).
  const resolveFragoRecipients = async (bountyRow) => {
    const userIds = new Set()
    if (bountyRow.target_type === 'faction' && bountyRow.target_faction_id) {
      const { data } = await supabase
        .from('event_players')
        .select('user_id')
        .eq('event_id', event.id)
        .eq('faction_id', bountyRow.target_faction_id)
      ;(data || []).forEach(p => userIds.add(p.user_id))
    } else {
      const { data: assigns } = await supabase
        .from('frago_assignments')
        .select('node_id, node:mcs_nodes(assigned_player_id)')
        .eq('bounty_id', bountyRow.id)
        .eq('status', 'active')
      const nodeIds = (assigns || []).map(a => a.node_id)
      ;(assigns || []).forEach(a => { if (a.node?.assigned_player_id) userIds.add(a.node.assigned_player_id) })
      if (nodeIds.length > 0) {
        const { data: members } = await supabase
          .from('mcs_node_members')
          .select('user_id')
          .in('node_id', nodeIds)
        ;(members || []).forEach(m => userIds.add(m.user_id))
      }
    }
    return [...userIds]
  }

  const completeFrago = async (bountyRow) => {
    if (!window.confirm(`Mark "${bountyRow.mission_name}" as MISSION SUCCESS?`)) return

    // Award points (best-effort sequential update — race condition possible
    // for concurrent FRAGOs; an RPC would be more robust but is out of MVP scope).
    const recipients = await resolveFragoRecipients(bountyRow)
    for (const uid of recipients) {
      const { data: ep } = await supabase
        .from('event_players')
        .select('session_points')
        .eq('event_id', event.id)
        .eq('user_id', uid)
        .limit(1)
      const next = (ep?.[0]?.session_points || 0) + (bountyRow.points || 0)
      await supabase
        .from('event_players')
        .update({ session_points: next })
        .eq('event_id', event.id)
        .eq('user_id', uid)
    }

    await supabase.from('bounties').update({ status: 'claimed', phase: 'resolved', claimed_at: new Date().toISOString() }).eq('id', bountyRow.id)
    await supabase.from('frago_assignments').update({ status: 'completed' }).eq('bounty_id', bountyRow.id).eq('status', 'active')
    await supabase.from('mission_events').insert({
      bounty_id: bountyRow.id,
      event_type: 'frago_completed',
      triggered_by: user.id,
      notes: `Awarded ${bountyRow.points} pts to ${recipients.length} player${recipients.length === 1 ? '' : 's'}`,
    })
    fetchBounties(); fetchFragoAssignments(); fetchPlayers()
  }

  const failFrago = async (bountyRow) => {
    if (!window.confirm(`Mark "${bountyRow.mission_name}" as MISSION FAILED?`)) return
    await supabase.from('bounties').update({ status: 'failed', phase: 'failed' }).eq('id', bountyRow.id)
    await supabase.from('frago_assignments').update({ status: 'failed' }).eq('bounty_id', bountyRow.id).eq('status', 'active')
    await supabase.from('mission_events').insert({
      bounty_id: bountyRow.id,
      event_type: 'frago_failed',
      triggered_by: user.id,
      notes: 'Mission failed',
    })
    fetchBounties(); fetchFragoAssignments()
  }

  // TD-27: cancel an in-flight FRAGO. Distinct from MISSION FAILED — cancel
  // means "scrap, never happened, exclude from stats" (the stats views
  // already filter out status='cancelled', shipped in the Sprint G fix).
  const cancelFrago = async (bountyRow) => {
    if (!window.confirm(`Cancel "${bountyRow.mission_name}" — scrap this FRAGO entirely (excluded from stats)?`)) return
    await supabase.from('bounties').update({ status: 'cancelled' }).eq('id', bountyRow.id)
    await supabase.from('frago_assignments').update({ status: 'failed' })
      .eq('bounty_id', bountyRow.id)
      .in('status', ['active','accepted','pursuing','delegated'])
    try {
      await supabase.from('mission_events').insert({
        bounty_id: bountyRow.id,
        event_type: 'frago_failed',
        triggered_by: user.id,
        notes: 'FRAGO cancelled by GM',
      })
    } catch { /* non-blocking */ }
    fetchBounties(); fetchFragoAssignments()
  }

  const activateFrago = async (bountyRow) => {
    if (!window.confirm(`Activate "${bountyRow.mission_name}" — make it live for assigned players?`)) return
    await supabase.from('bounties').update({ status: 'active', phase: 'active' }).eq('id', bountyRow.id)
    await supabase.from('mission_events').insert({
      bounty_id: bountyRow.id,
      event_type: 'frago_activated',
      triggered_by: user.id,
      notes: 'Queued FRAGO activated',
    })
    fetchBounties(); fetchFragoAssignments()
  }

  const resendFrago = async (bountyRow) => {
    if (!window.confirm(`Resend "${bountyRow.mission_name}" — reset to active?`)) return
    await supabase.from('bounties').update({ status: 'active', phase: 'active' }).eq('id', bountyRow.id)
    await supabase.from('frago_assignments').update({ status: 'active' }).eq('bounty_id', bountyRow.id).eq('status', 'failed')
    await supabase.from('mission_events').insert({
      bounty_id: bountyRow.id,
      event_type: 'frago_resent',
      triggered_by: user.id,
      notes: 'FRAGO reissued for retry',
    })
    fetchBounties(); fetchFragoAssignments()
  }

  // Promote a 'pursuing' assignment into a formal 'active' delegation, and
  // clear out any other pursuit rows on the same FRAGO. Mirrors the
  // claim-a-broadcast flow — first node to commit and get GM's nod becomes
  // the owner, the rest stand down automatically.
  const promotePursuit = async (assignment) => {
    if (!window.confirm(`Delegate "${assignment.bounty?.mission_name || 'this FRAGO'}" to the pursuing node?`)) return
    await supabase.from('frago_assignments').update({ status: 'active' }).eq('id', assignment.id)
    // Clear sibling pursuits for the same bounty (stand-down everyone else).
    await supabase.from('frago_assignments').delete()
      .eq('bounty_id', assignment.bounty_id)
      .eq('status', 'pursuing')
      .neq('id', assignment.id)
    // Bounty is now formally delegated, not just broadcast.
    await supabase.from('bounties').update({ target_type: 'node' }).eq('id', assignment.bounty_id)
    await supabase.from('mission_events').insert({
      bounty_id: assignment.bounty_id,
      event_type: 'frago_delegated',
      triggered_by: user.id,
      notes: `Promoted from pursuit → delegated to node ${assignment.node?.name || assignment.node_id}`,
    })
    fetchBounties(); fetchFragoAssignments()
  }

  const reassignFrago = async (assignment, newNodeId) => {
    if (!newNodeId || newNodeId === assignment.node_id) return
    await supabase.from('frago_assignments').update({ status: 'reassigned' }).eq('id', assignment.id)
    await supabase.from('frago_assignments').insert({
      bounty_id: assignment.bounty_id,
      node_id: newNodeId,
      assigned_by: user.id,
      status: 'active',
    })
    await supabase.from('mission_events').insert({
      bounty_id: assignment.bounty_id,
      event_type: 'frago_reassigned',
      triggered_by: user.id,
      notes: `Reassigned from node ${assignment.node?.name || assignment.node_id} → ${mcsNodes.find(n => n.id === newNodeId)?.name || newNodeId}`,
    })
    fetchFragoAssignments()
  }
  const fetchRecentActivity = async () => {
    // Scope mission events to bounties this viewer can see — visibleBounties
    // already filters out the opposing faction's bounties for a co-GM, so
    // sourcing bounty IDs from there keeps the activity feed faction-locked.
    // Drop queued / draft items so setup events (creation, edits during
    // build-out) don't pollute the SITREP timeline — that's GM workshop
    // activity, not gameplay.
    const sourceBounties = (isCoGm ? visibleBounties : bounties)
      .filter(b => b && !['queued', 'draft'].includes(b.status))
    if (sourceBounties.length === 0 && !isCoGm) { setRecentActivity([]); return }
    const bountyIds = sourceBounties.map(b => b.id)
    const [me, kl, bml, csl] = await Promise.all([
      bountyIds.length === 0
        ? Promise.resolve({ data: [] })
        : supabase.from('mission_events')
            .select('id, bounty_id, event_type, notes, triggered_by, created_at, bounty:bounties(mission_name, bounty_type)')
            .in('bounty_id', bountyIds)
            .order('created_at', { ascending: false })
            .limit(20),
      supabase.from('kill_log')
        .select('id, status, kill_type, scanned_at, killer_id, target_id, killer:users!kill_log_killer_id_fkey(callsign, display_name), target:users!kill_log_target_id_fkey(callsign, display_name)')
        .eq('event_id', event.id)
        .order('scanned_at', { ascending: false })
        .limit(20),
      // ANU-121: BLIND MAN calls are significant game events — include in SITREP.
      // blind_man_log is GM/IGA-read-only by RLS; this fetch silently returns [] for co-GMs
      // without that role (they'd see no rows, which is fine — BLIND MAN is a GM action).
      supabase.from('blind_man_log')
        .select('id, called_at, called_by, reason, resumed_at, resumed_by, duration_seconds, caller:users!blind_man_log_called_by_fkey(callsign, display_name), resumer:users!blind_man_log_resumed_by_fkey(callsign, display_name)')
        .eq('event_id', event.id)
        .order('called_at', { ascending: false })
        .limit(10),
      // ANU-164: checkpoint scans. checkpoints already fetched into state; pull recent
      // scans scoped to this event's checkpoint IDs.
      (checkpoints || []).length > 0
        ? supabase.from('checkpoint_scans')
            .select('id, scanned_at, scan_type, faction_id, checkpoint:event_checkpoints!checkpoint_scans_checkpoint_id_fkey(name), scanner:users!checkpoint_scans_scanned_by_fkey(callsign, display_name)')
            .in('checkpoint_id', (checkpoints || []).map(cp => cp.id))
            .order('scanned_at', { ascending: false })
            .limit(20)
        : Promise.resolve({ data: [] }),
    ])
    // Co-GM kill-feed scope: keep a kill if either the killer or the target
    // belongs to this viewer's faction. Cross-faction kills involving the
    // co-GM's faction are intentionally still in scope (one of their players
    // got hit / scored).
    const killRows = isCoGm
      ? (kl.data || []).filter(k =>
          playerFactionMap[k.killer_id] === coGmFactionId ||
          playerFactionMap[k.target_id] === coGmFactionId
        )
      : (kl.data || [])
    const blindManRows = (bml.data || []).flatMap(b => {
      const callerLabel = b.caller?.callsign || b.caller?.display_name || 'referee'
      const entries = [{ ts: b.called_at, kind: 'blind_man', text: `🔴 BLIND MAN — ${callerLabel}${b.reason ? ` · ${b.reason}` : ''}` }]
      if (b.resumed_at) {
        const resumerLabel = b.resumer?.callsign || b.resumer?.display_name || 'referee'
        entries.push({ ts: b.resumed_at, kind: 'blind_man', text: `🟢 BLIND MAN lifted — ${resumerLabel}${b.duration_seconds ? ` (${b.duration_seconds}s)` : ''}` })
      }
      return entries
    })
    const combined = [
      // phase_change is logged at bounty-creation time for DEFEND bounties —
      // it's setup noise, not live gameplay. Exclude it from the activity feed.
      ...(me.data || []).filter(e => e.event_type !== 'phase_change').map(e => ({
        ts: e.created_at,
        kind: 'mission',
        text: `${e.event_type.replace(/_/g, ' ')}${e.bounty?.mission_name ? ` — ${e.bounty.mission_name}` : e.bounty?.bounty_type ? ` (${e.bounty.bounty_type})` : ''}${e.notes ? `: ${e.notes}` : ''}`,
      })),
      ...killRows.map(k => {
        const isContact = k.kill_type === 'faction_kill' || k.kill_type === 'team_kill'
        return {
          ts: k.scanned_at,
          kind: isContact ? 'contact' : 'kill',
          text: `${isContact ? '◆ CONTACT' : '⊕ KILL'} — ${k.killer?.callsign || k.killer?.display_name || 'Player'} → ${k.target?.callsign || k.target?.display_name || 'target'}${k.status !== 'pending' ? ` · ${k.status}` : ''}`,
        }
      }),
      ...(csl.data || []).map(cs => ({
        ts: cs.scanned_at,
        kind: 'checkpoint',
        text: `📍 CHECKPOINT — ${cs.scanner?.callsign || cs.scanner?.display_name || 'Player'} · ${cs.checkpoint?.name || 'Unknown'} · ${(cs.scan_type || '').replace(/_/g, ' ')}`,
      })),
      ...blindManRows,
    ].sort((a, b) => new Date(b.ts) - new Date(a.ts)).slice(0, 10)
    setRecentActivity(combined)
  }
  // ANU-39 — persist start/end timestamps. mutate() flushes the events
  // cache so the SWR read on next mount lands fresh, and the in-component
  // refresh keeps the header strip in sync without a remount.
  const saveSchedule = async ({ starts_at, ends_at }) => {
    const { error } = await mutate('events', () =>
      supabase.from('events').update({ starts_at, ends_at }).eq('id', event.id)
    )
    if (error) {
      alert('Save failed: ' + (error.message || 'unknown'))
      return false
    }
    setEventData(prev => ({ ...prev, starts_at, ends_at }))
    return true
  }

  // Track 1.2 — roster mutations route through mutate() so the
  // event_players / event_factions / events caches flush on success.
  const removePlayer = async (userId) => {
    if (!window.confirm('Remove player?')) return
    await mutate('event_players', () =>
      supabase.from('event_players').delete().eq('event_id', event.id).eq('user_id', userId)
    )
    fetchPlayers()
  }
  const updatePlayerFaction = async (userId, factionId) => {
    await mutate('event_players', () =>
      supabase.from('event_players').update({ faction_id: factionId || null }).eq('event_id', event.id).eq('user_id', userId)
    )
    fetchPlayers()
  }
  const approvePlayer = async (userId) => {
    await mutate('event_players', () =>
      supabase.from('event_players').update({ reg_status: 'approved' }).eq('event_id', event.id).eq('user_id', userId)
    )
    fetchPlayers()
  }
  // GM Access dropdown writes the role + co_gm_faction_id pair atomically.
  // updatePlayerRole(uid, 'co_gm', factionId) → co-GM scoped to that faction.
  // updatePlayerRole(uid, 'gm', null) → full GM. updatePlayerRole(uid, 'player', null)
  // → demote. delegate_scope is wiped on every transition since the legacy
  // delegate role is OBE.
  const updatePlayerRole = async (userId, newRole, coGmFactionId = null) => {
    // Track 1.2 — role flips touch event_players + downstream
    // event-level caches via TABLE_CACHE_MAP.
    await mutate('event_players', () =>
      supabase
        .from('event_players')
        .update({
          event_role: newRole,
          co_gm_faction_id: newRole === 'co_gm' ? coGmFactionId : null,
          delegate_scope: null,
        })
        .eq('event_id', event.id)
        .eq('user_id', userId)
    )
    fetchPlayers()
  }

  const downloadEventQR = () => {
    const svg = document.getElementById('event-qr-code'); const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas'); canvas.width = 400; canvas.height = 480; const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#000000'; ctx.fillRect(0, 0, canvas.width, canvas.height)
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 50, 40, 300, 300)
      ctx.fillStyle = '#00ff66'; ctx.font = 'bold 28px monospace'; ctx.textAlign = 'center'; ctx.fillText(eventData.name, 200, 370)
      ctx.fillStyle = '#00ff66'; ctx.font = 'bold 24px monospace'; ctx.fillText(eventData.join_code, 200, 410)
      ctx.fillStyle = '#4aaa5a'; ctx.font = 'bold 14px monospace'; ctx.fillText('DEATH CARD', 200, 455)
      const link = document.createElement('a'); link.download = `death-card-event-${eventData.join_code}.png`; link.href = canvas.toDataURL('image/png'); link.click()
    }
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' }); img.src = URL.createObjectURL(svgBlob)
  }

  const printEventQR = () => { const svgString = serializeSvgFromDom('event-qr-code'); if (!svgString) return; printQR({ svgString, title: eventData.name, code: eventData.join_code, instructions: 'Scan to join', url: `https://death-card.vercel.app/join/${eventData.join_code}` }) }

  const subscribeToChanges = () => {
    // ANU-125: expire_bounties removed from client — pg_cron (expire-bounties, every 1 min) handles it server-side.
    const channel = supabase.channel(`event-${event.id}`).on('postgres_changes', { event: '*', schema: 'public', table: 'bounties', filter: `event_id=eq.${event.id}` }, () => fetchBounties()).subscribe()
    return () => { supabase.removeChannel(channel) }
  }

  const fetchEquipment = async () => {
    const { data } = await supabase.from('event_equipment')
      .select('id, name, status').eq('event_id', event.id).eq('status', 'active')
      .order('created_at')
    setEquipment(data || [])
  }

  // Pull the soonest un-deployed scheduled item + any 'found' intel
  // triggers (those awaiting commander action). Both feed SITREP.
  const fetchIntelAndDeployments = async () => {
    const [schRes, intelRes] = await Promise.all([
      supabase.from('scheduled_deployments')
        .select('*, bounty:bounties(id, mission_name, points, bounty_type)')
        .eq('event_id', event.id).eq('deployed', false)
        .order('deploy_at').limit(1),
      supabase.from('intel_triggers')
        .select('*, linked_bounty:bounties(id, mission_name, points, status)')
        .eq('event_id', event.id).eq('status', 'found')
        .order('created_at', { ascending: false }),
    ])
    setNextDeployment(schRes.data?.[0] || null)
    setPendingIntel(intelRes.data || [])
  }

  // Phase 6 deploy engine: any scheduled_deployment where deploy_at <= now()
  // and deployed=false fires its activation. cadre_queue → awaiting_delegation,
  // broadcast → active.
  const processDueDeployments = async () => {
    const { data } = await supabase.from('scheduled_deployments')
      .select('id, linked_bounty_id, target_type, target_faction_id, deploy_at')
      .eq('event_id', event.id).eq('deployed', false)
      .lte('deploy_at', new Date().toISOString())
    for (const sched of (data || [])) {
      const newStatus = sched.target_type === 'cadre_queue' ? 'awaiting_delegation' : 'active'
      await supabase.from('bounties').update({ status: newStatus, phase: 'active' }).eq('id', sched.linked_bounty_id)
      await supabase.from('scheduled_deployments').update({
        deployed: true, deployed_at: new Date().toISOString(),
      }).eq('id', sched.id)
      try {
        await supabase.from('mission_events').insert({
          bounty_id: sched.linked_bounty_id,
          event_type: 'scheduled_deployment',
          triggered_by: user.id,
          faction_id: sched.target_faction_id,
          notes: `Auto-deployed at ${new Date(sched.deploy_at).toISOString()}`,
        })
      } catch { /* non-blocking */ }
    }
    if ((data || []).length > 0) fetchBounties()
  }

  // Commander acknowledgement actions — mark trigger 'spent' after handling.
  const ackIntelDelegate = async (intel, nodeId) => {
    if (!nodeId || !intel.linked_bounty_id) return
    // Insert a frago_assignments row for the chosen node. Surface the error
    // here (unlike the mission_events trail below) — if the assignment
    // doesn't land, the FRAGO never reaches the node and the GM gets no
    // signal otherwise. supabase-js builders are thenables; use try/await.
    try {
      const { error: faErr } = await supabase.from('frago_assignments').insert({
        bounty_id: intel.linked_bounty_id,
        node_id: nodeId,
        assigned_by: user.id,
        status: 'active',
      })
      if (faErr) { alert('Delegate failed: ' + faErr.message); return }
    } catch (err) { alert('Delegate failed: ' + (err?.message || 'unknown')); return }
    await supabase.from('bounties').update({ status: 'active', phase: 'active' }).eq('id', intel.linked_bounty_id)
    await supabase.from('intel_triggers').update({ status: 'spent' }).eq('id', intel.id)
    try {
      await supabase.from('mission_events').insert({
        bounty_id: intel.linked_bounty_id,
        event_type: 'frago_delegated',
        triggered_by: user.id,
        notes: 'Delegated from intel-find',
      })
    } catch { /* non-blocking */ }
    fetchBounties(); fetchIntelAndDeployments(); fetchFragoAssignments()
  }
  const ackIntelBroadcast = async (intel) => {
    if (!intel.linked_bounty_id) return
    await supabase.from('bounties').update({ status: 'active', phase: 'active', target_type: 'faction' }).eq('id', intel.linked_bounty_id)
    await supabase.from('intel_triggers').update({ status: 'spent' }).eq('id', intel.id)
    fetchBounties(); fetchIntelAndDeployments()
  }
  const ackIntelIgnore = async (intel) => {
    await supabase.from('intel_triggers').update({ status: 'spent' }).eq('id', intel.id)
    fetchIntelAndDeployments()
  }

  // All forward + admin transitions go through the RPC, which validates
  // the state machine, gates per-transition permissions, stamps the
  // matching transition timestamp, and writes event_status_log.
  const transitionStatus = async (newStatus, opts = {}) => {
    const { destructive, confirmMessage } = opts
    if (destructive && !window.confirm(confirmMessage || `Transition event to ${newStatus}?`)) return
    // Track 1.2 — transition_event_status is an RPC, so it doesn't fire
    // postgres_changes. dbMutate.rpc routes through the same TABLE_CACHE_MAP
    // so the events cache flushes and downstream SWR reads land fresh.
    const transitionResult = await rpc('transition_event_status', {
      p_event_id: event.id,
      p_new_status: newStatus,
    }, { tables: ['events'] })
    if (transitionResult.error) {
      alert('Status change rejected: ' + (transitionResult.friendlyMessage || transitionResult.error.message))
      return
    }
    // Re-read the event row so transition timestamps + status update.
    const { data } = await supabase.from('events').select('*').eq('id', event.id).limit(1)
    if (data?.[0]) setEventData(data[0])
    // Closing the event expires any leftover live bounties.
    if (newStatus === 'closed') {
      await mutate('bounties', () =>
        supabase.from('bounties').update({ status: 'expired' }).eq('event_id', event.id).eq('status', 'active')
      )
      fetchBounties()
    }
  }

  // Active pause = an event_pauses row whose actual_start is set and
  // actual_end is null. Polled with the event refresh, also drives the
  // "GAME PAUSED" banner across player views.
  const fetchActivePause = async () => {
    const { data } = await supabase.from('event_pauses')
      .select('*').eq('event_id', event.id)
      .is('actual_end', null).not('actual_start', 'is', null)
      .order('actual_start', { ascending: false }).limit(1)
    setActivePause(data?.[0] || null)
  }

  const fetchScheduledBreaks = async () => {
    const { data } = await supabase.from('event_pauses')
      .select('*').eq('event_id', event.id).eq('pause_type', 'scheduled')
      .order('scheduled_start', { ascending: true })
    setScheduledBreaks(data || [])
  }
  const addBreak = async () => {
    if (!breakDraft.name || !breakDraft.start || !breakDraft.end) return
    if (new Date(breakDraft.end) <= new Date(breakDraft.start)) { alert('Break end must be after start.'); return }
    await supabase.from('event_pauses').insert({
      event_id: event.id, pause_type: 'scheduled', reason: breakDraft.name,
      scheduled_start: new Date(breakDraft.start).toISOString(),
      scheduled_end: new Date(breakDraft.end).toISOString(), created_by: user.id,
    })
    setBreakDraft({ name: '', start: '', end: '' }); setShowAddBreak(false); fetchScheduledBreaks()
  }
  const deleteBreak = async (id) => {
    await supabase.from('event_pauses').delete().eq('id', id); fetchScheduledBreaks()
  }

  const pauseGame = async () => {
    const reason = window.prompt('Reason for pause? (optional)') || null
    const { error } = await supabase.from('event_pauses').insert({
      event_id: event.id,
      pause_type: 'manual',
      actual_start: new Date().toISOString(),
      reason,
      created_by: user.id,
    })
    if (error) { alert('Pause failed: ' + error.message); return }
    fetchActivePause()
  }

  const resumeGame = async () => {
    if (!activePause) return
    const { error } = await supabase.from('event_pauses')
      .update({ actual_end: new Date().toISOString() })
      .eq('id', activePause.id)
    if (error) { alert('Resume failed: ' + error.message); return }
    fetchActivePause()
  }

  const handleSignOut = async () => {
    await clearAllUserState()
    await supabase.auth.signOut()
  }

  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }))
  const st = statusTag[eventData.status] || statusTag.closed

  return (
    <div style={C.page}>
      <style>{`@keyframes cipher-blink { 50% { opacity: 0; } }`}</style>

      {/* ANU-91 — GM resume banner. Sits at the very top so it's hard to
          miss. Renders for any GM/co-GM viewing the event while is_paused
          is true. Only full GMs (isFullGM = m55 staff or direct event GM)
          can actually lift the ceasefire — co-GMs see the banner but no
          RESUME button. We do NOT mount the full-screen BlindManOverlay
          here on purpose: GMs need to be able to interact with this UI
          to end the ceasefire. The overlay covers PlayerView and
          RefereeView for everyone else. */}
      {blindMan.isPaused && (
        <div style={{
          background: 'linear-gradient(90deg, #cc0000, #ff2a2a)',
          color: '#fff',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          borderBottom: '2px solid #ff6060',
          boxShadow: '0 0 16px rgba(255,42,42,.35)',
          position: 'relative',
          zIndex: 30,
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 14, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              🔴 BLIND MAN ACTIVE — GAME PAUSED
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, opacity: 0.92 }}>
              {blindMan.callerCallsign ? `Called by ${blindMan.callerCallsign}` : 'Caller unknown'}
              {' · '}
              {(() => {
                const m = Math.floor(blindManElapsed / 60)
                const s = blindManElapsed % 60
                return `T+${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
              })()}
              {blindMan.reason && ` · ${blindMan.reason}`}
            </span>
          </div>
          {(isFullGM || isCoGm) && (
            <button onClick={resumeBlindMan} disabled={resumeBusy}
              style={{
                background: '#fff',
                color: '#cc0000',
                border: 'none',
                padding: '10px 18px',
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                cursor: resumeBusy ? 'wait' : 'pointer',
                minHeight: 44,
                flexShrink: 0,
              }}>
              {resumeBusy ? 'RESUMING...' : '▶ RESUME GAME'}
            </button>
          )}
        </div>
      )}

      {/* ── NAV ── */}
      <nav style={C.nav}>
        <div className="flex items-center gap-3 min-w-0">
          {/* Track 6.2 — 44x44 min hit area on the nav back button. The
              ArrowLeft icon alone was an 18×18 hit target which is hard
              to tap on a phone. */}
          <button onClick={onBack} style={{
            background: 'none', border: 'none', color: '#4aaa5a', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minHeight: '44px', minWidth: '44px', padding: '8px',
          }}><ArrowLeft size={18} /></button>
          <div className="min-w-0">
            <h1 className="truncate" style={{ ...C.display, fontSize: '14px', margin: 0 }}>{event.name}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              <span style={{ ...C.tag, ...st }}>{eventData.status === 'live' ? '● LIVE' : eventData.status.toUpperCase()}</span>
            </div>
            {/* ANU-39 — start/end schedule strip. Edit affordance is gated
                on isSettingsEditable so it disappears once registration
                opens (per UX-4); read-only display stays for everyone. */}
            {/* ANU-115: schedule editing moved to SETTINGS tab */}
            <EventScheduleStrip
              event={eventData}
              canEdit={false}
              onSave={saveSchedule}
            />
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0 items-center relative">
          {/* Pinned: NotificationBell leftmost in right group */}
          <NotificationBell tone="cipher" />
          {/* BLIND MAN pinned, always visible */}
          {isFullGM && eventData.status === 'live' && !blindMan.isPaused && (
            <CallBlindManButton eventId={eventData.id} compact />
          )}
          {/* Hamburger — all viewports */}
          <button
            onClick={() => setNavMenuOpen(o => !o)}
            style={{ background: '#0a1a0a', color: '#00ff66', border: '1px solid rgba(0,255,102,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, flexShrink: 0 }}
            aria-label="More actions"
          >
            <Menu size={16} />
          </button>
        </div>
      </nav>

      {/* ANU-124: drawer outside <nav> — nav has position:relative+zIndex which creates a stacking
          context; fixed children inside it are confined to that context and lose to <main> at
          the same z-index level. Sibling placement escapes the context. */}
      {navMenuOpen && (
        <>
          <div
            onClick={() => setNavMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.3)' }}
          />
          <div
            style={{
              position: 'fixed', top: 60, right: 12, zIndex: 10001,
              background: '#0a140a', border: '1px solid rgba(0,255,102,.2)',
              padding: 8, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 4,
              boxShadow: '0 8px 20px rgba(0,0,0,.6)',
            }}
          >
            <ViewSwitcher currentView="gm" />
            <button
              onClick={() => { setNavMenuOpen(false); setShowShareQR(true) }}
              style={{ ...C.btnSecondary, width: '100%', minHeight: 44, padding: '0 12px', gap: 8, justifyContent: 'flex-start', display: 'flex', alignItems: 'center' }}
            >
              <QrCodeIcon size={14} />
              <span style={{ fontSize: 12, fontFamily: "'VT323', monospace" }}>EVENT QR</span>
            </button>
            {isFullGM && NEXT_STEP[eventData.status] && (() => {
              const step = NEXT_STEP[eventData.status]
              const buttonStyle = step.destructive
                ? (step.target === 'live' ? C.btnPrimary
                  : step.target === 'endex' ? C.btnDanger
                  : step.target === 'closed' ? C.btnDanger
                  : C.btnStatus('rgba(77,160,255,.15)', '#4da0ff'))
                : C.btnStatus('rgba(255,204,51,.15)', '#ffcc33')
              return (
                <button onClick={() => { setNavMenuOpen(false); transitionStatus(step.target, {
                  destructive: step.destructive,
                  confirmMessage: `${step.label}? This moves the event from ${eventData.status} to ${step.target}.`,
                }) }} style={{ ...buttonStyle, width: '100%', minHeight: 44, padding: '0 12px', gap: 8, justifyContent: 'flex-start', display: 'flex', alignItems: 'center' }}>
                  {step.icon === 'lock' ? <Lock size={14} /> :
                   step.icon === 'live' ? <Radio size={14} /> :
                   step.icon === 'stop' ? <XCircle size={14} /> :
                   step.icon === 'close' ? <XCircle size={14} /> : null}
                  <span style={{ fontSize: 12, fontFamily: "'VT323', monospace" }}>{step.label}</span>
                </button>
              )
            })()}
            {eventData.status === 'live' && !activePause && (
              <button onClick={() => { setNavMenuOpen(false); pauseGame() }} style={{ ...C.btnStatus('rgba(255,204,51,.15)', '#ffcc33'), width: '100%', minHeight: 44, padding: '0 12px', gap: 8, justifyContent: 'flex-start' }}>
                <span style={{ fontSize: 16 }}>⏸</span>
                <span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>PAUSE GAME</span>
              </button>
            )}
            {eventData.status === 'live' && activePause && (
              <button onClick={() => { setNavMenuOpen(false); resumeGame() }} style={{ ...C.btnPrimary, width: '100%', minHeight: 44, padding: '0 12px', gap: 8, justifyContent: 'flex-start' }}>
                <span style={{ fontSize: 16 }}>▶</span>
                <span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>RESUME</span>
              </button>
            )}
            {/* ANU-182 — FIELD INTEL runtime kill switch in mobile drawer */}
            {isFullGM && eventData.status === 'live' && (() => {
              const locked = !orgFieldIntelEnabled || !eventData.field_intel_enabled
              const on = eventData.field_intel_runtime_enabled
              return (
                <button
                  disabled={locked}
                  onClick={async () => {
                    if (locked) return
                    setNavMenuOpen(false)
                    const next = !on
                    await supabase.from('events').update({ field_intel_runtime_enabled: next }).eq('id', eventData.id)
                    setEventData(prev => ({ ...prev, field_intel_runtime_enabled: next }))
                  }}
                  style={{
                    ...C.btnStatus(on ? 'rgba(0,255,102,.08)' : 'rgba(255,42,42,.08)', on ? '#00ff66' : '#ff2a2a'),
                    width: '100%', minHeight: 44, padding: '0 12px', gap: 8, justifyContent: 'flex-start',
                    opacity: locked ? 0.4 : 1, cursor: locked ? 'not-allowed' : 'pointer',
                  }}
                  title={locked ? 'Field intel disabled at org or event level' : on ? 'Disable field intel' : 'Enable field intel'}
                >
                  <span style={{ fontSize: 14 }}>{on ? '▣' : '□'}</span>
                  <span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>
                    FIELD INTEL {on ? 'ON' : 'OFF'}
                  </span>
                </button>
              )
            })()}
            {isFullGM && eventData.status === 'locked' && (
              <button
                onClick={() => { setNavMenuOpen(false); transitionStatus('registration', { destructive: true, confirmMessage: 'Unlock event and reopen registration?' }) }}
                style={{ ...C.btnSecondary, width: '100%', minHeight: 44, padding: '0 12px', gap: 8, justifyContent: 'flex-start' }}
              >
                <Unlock size={14} />
                <span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>UNLOCK</span>
              </button>
            )}
            {isFullGM && ['draft','prep','registration','locked'].includes(eventData.status) && (
              <button
                onClick={() => { setNavMenuOpen(false); transitionStatus('cancelled', { destructive: true, confirmMessage: 'Cancel this event? All queued bounties + FRAGOs will be cancelled. This cannot be undone.' }) }}
                style={{ ...C.btnStatus('rgba(255,42,42,.15)', '#ff2a2a'), width: '100%', minHeight: 44, padding: '0 12px', gap: 8, justifyContent: 'flex-start' }}
              >
                <XCircle size={14} />
                <span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>CANCEL EVENT</span>
              </button>
            )}
            {eventData.status === 'closed' && isPlatformAdmin(user) && (
              <button
                onClick={() => { setNavMenuOpen(false); transitionStatus('endex', { destructive: true, confirmMessage: 'Reopen closed event for data correction?' }) }}
                style={{ ...C.btnSecondary, width: '100%', minHeight: 44, padding: '0 12px', gap: 8, justifyContent: 'flex-start' }}
              >
                <span style={{ fontSize: 14 }}>↻</span>
                <span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>REOPEN</span>
              </button>
            )}
            <div style={{ borderTop: '1px solid rgba(0,255,102,.12)', marginTop: 4, paddingTop: 4 }}>
              <button
                onClick={() => { setNavMenuOpen(false); handleSignOut() }}
                style={{ ...C.btnSecondary, width: '100%', minHeight: 44, padding: '0 12px', gap: 8, justifyContent: 'flex-start', color: '#4a8a5a' }}
              >
                <LogOut size={14} />
                <span style={{ fontSize: 12, fontFamily: "'VT323', monospace", letterSpacing: '0.1em' }}>SIGN OUT</span>
              </button>
            </div>
          </div>
        </>
      )}

      {showShareQR && <ShareQR event={eventData} factionId={players.find(p => p.user_id === user?.id)?.faction_id} onClose={() => setShowShareQR(false)} />}
      {showBulkQR && <QRDownloadCenter event={eventData} onClose={() => setShowBulkQR(false)} />}

      {/* GAME PAUSED banner — visible to GM whenever a pause is active. */}
      {activePause && (
        <div style={{
          background: 'rgba(255,204,51,.15)', borderTop: '1px solid #ffcc33',
          borderBottom: '1px solid #ffcc33', padding: '10px 16px', textAlign: 'center',
          color: '#ffcc33', textShadow: '0 0 6px rgba(255,204,51,.6)',
          fontFamily: "'VT323', monospace", letterSpacing: '0.18em', fontSize: 16,
        }}>
          ⏸ GAME PAUSED{activePause.reason ? ` — ${activePause.reason}` : ''} · since {new Date(activePause.actual_start).toLocaleTimeString()}
        </div>
      )}

      <main className="max-w-4xl mx-auto p-4" style={{ position: 'relative', zIndex: 10 }}>
        {/* Faction balance is a global property — only meaningful for full
            GMs. Co-GMs scoped to one faction don't need cross-faction parity
            warnings. */}
        {isFullGM && <FactionBalanceWarning players={players} factions={factions} />}

        {/* ── TABS ── */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { id: 'sitrep', icon: Activity, label: 'SITREP', count: null },
            // Play sheet only shows during prep + live; pre-prep there's
            // nothing to deploy yet. Co-GMs whose play_sheet_mode = 'off'
            // are filtered downstream.
            ...((['prep','live'].includes(eventData.status) && (isFullGM || (isCoGm && (orgFeatures.play_sheet_mode || 'limited') !== 'off')))
              ? [{ id: 'play_sheet', icon: CalendarIcon, label: 'PLAY SHEET', count: null }]
              : []),
            { id: 'bounties', icon: Target, label: 'BOUNTIES', count: visibleBounties.filter(b => b.category !== 'frago' && b.status === 'active').length },
            { id: 'fragos', icon: FileText, label: 'FRAGOS', count: visibleBounties.filter(b => b.category === 'frago' && b.status === 'active').length },
            // ANU-6 — equipment + checkpoint locations are intel a co-GM
            // who's also playing shouldn't see. Hide the tab; co-GMs can
            // still reference equipment by name in the FRAGO builder.
            // Full GMs and platform staff still see it (isFullGM covers
            // canManageEvent: m55 staff OR direct event GM).
            ...(isFullGM ? [{ id: 'gear', icon: Eye, label: 'GEAR', count: null }] : []),
            { id: 'players', icon: Users, label: 'ROSTER', count: players.length },
            // ANU-89 — war crime reports. Visible to full GM + co-GMs (RLS
            // also covers IGAs but they live in RefereeView, not here).
            { id: 'war_crimes', icon: ShieldAlert, label: 'WAR CRIMES', count: warCrimesPending || null },
            { id: 'mcs', icon: Network, label: 'MCS', count: null },
            { id: 'event_qr', icon: QrCodeIcon, label: 'QR', count: null },
            // ANU-201 — doctrine template library (FRAGO + bounty templates → DEPLOY).
            ...(isGm ? [{ id: 'doctrine', icon: BookOpen, label: 'DOCTRINE', count: null }] : []),
            // ANU-115 — settings tab for GM + co_gm. Full edit for GM only.
            ...(isGm ? [{ id: 'settings', icon: Settings, label: 'SETTINGS', count: null }] : []),
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={C.tab(activeTab === t.id)}>
              {t.icon && <t.icon size={14} />}
              <span className="hidden xs:inline">{t.label}</span>
              {t.count !== null && <span>({t.count})</span>}
            </button>
          ))}
        </div>

        {/* ── SITREP TAB ── */}
        {activeTab === 'sitrep' && (() => {
          const nonFragoBounties = visibleBounties.filter(b => b.category !== 'frago')
          const fragoBounties = visibleBounties.filter(b => b.category === 'frago')
          return (
            <div className="space-y-4">
              {/* WARNO — single next upcoming scheduled deployment */}
              {nextDeployment && !nextDeployment.deployed && (() => {
                const due = new Date(nextDeployment.deploy_at)
                const minLeft = Math.round((due - Date.now()) / 60000)
                const overdue = minLeft <= 0
                return (
                  <Brackets className="p-3" style={{ borderColor: overdue ? '#ff2a2a' : '#ffcc33' }}>
                    <p style={{ ...C.mono, fontSize: 10, color: '#ffcc33', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                      ⚠ WARNO
                    </p>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p style={{ ...C.display, fontSize: 14, color: '#ffcc33' }}>
                          {nextDeployment.bounty?.mission_name || nextDeployment.bounty?.bounty_type || 'mission'} deploys
                          {' '}{overdue ? <span style={{ color: '#ff2a2a' }}>NOW</span> : `in ${minLeft}m`}
                        </p>
                        <p style={{ ...C.mono, fontSize: 10, color: '#9aa6b0', marginTop: 2 }}>
                          {nextDeployment.target_type}
                          {nextDeployment.target_faction_id ? ` · ${factions.find(f => f.id === nextDeployment.target_faction_id)?.name || 'faction'}` : ''}
                          {' · '}{due.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      {isGm && (
                        <button onClick={async () => {
                          if (!window.confirm('Deploy this FRAGO now?')) return
                          const newStatus = nextDeployment.target_type === 'cadre_queue' ? 'awaiting_delegation' : 'active'
                          await supabase.from('bounties').update({ status: newStatus, phase: 'active' }).eq('id', nextDeployment.linked_bounty_id)
                          await supabase.from('scheduled_deployments').update({ deployed: true, deployed_at: new Date().toISOString() }).eq('id', nextDeployment.id)
                          try {
                            await supabase.from('mission_events').insert({
                              bounty_id: nextDeployment.linked_bounty_id,
                              event_type: 'scheduled_deployment',
                              triggered_by: user.id,
                              faction_id: nextDeployment.target_faction_id,
                              notes: 'Manually deployed via WARNO',
                            })
                          } catch { /* non-blocking */ }
                          fetchBounties(); fetchIntelAndDeployments()
                        }} style={{ ...C.btnPrimary, fontSize: 11 }}>
                          <Radio size={11} /> DEPLOY NOW
                        </button>
                      )}
                    </div>
                  </Brackets>
                )
              })()}

              {/* Commander notification — intel found, awaiting action (Phase 4) */}
              {pendingIntel.length > 0 && (
                <div className="space-y-2">
                  {pendingIntel.map(intel => {
                    // Co-GMs only delegate within their own faction (Bug 30);
                    // visibleMcsNodes is already scoped above.
                    const childNodes = visibleMcsNodes.filter(n => n.event_id === event.id)
                    return (
                      <Brackets key={intel.id} className="p-3" style={{ borderColor: '#ffcc33' }}>
                        {/* Phase 5C of the FRAGO Rework — relabel the SITREP
                            banner from the legacy "intel found" framing to
                            the unified "FRAGO triggered" one. Mounts a
                            compact phase strip whenever the linked bounty
                            is a structured FRAGO, so the GM sees phase
                            progression at a glance. */}
                        <p style={{ ...C.mono, fontSize: 10, color: '#ffcc33', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                          ◆ FRAGO TRIGGERED — AWAITING ACTION
                        </p>
                        <p style={{ ...C.mono, fontSize: 12, color: '#e0e8de' }}>{intel.commander_message || intel.message}</p>
                        {intel.linked_bounty && (
                          <>
                            <p style={{ ...C.mono, fontSize: 11, color: '#9aa6b0', marginTop: 4 }}>
                              Linked: {intel.linked_bounty.mission_name || 'mission'}{!hideScoresForViewer ? ` · ${intel.linked_bounty.points} pts` : ''} · {intel.linked_bounty.status}
                            </p>
                            <div className="mt-2">
                              <FragoPhaseStepper fragoId={intel.linked_bounty.id} variant="compact" />
                            </div>
                          </>
                        )}
                        {isGm && intel.linked_bounty_id && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            <select onChange={e => { if (e.target.value) ackIntelDelegate(intel, e.target.value); e.target.value = '' }} value=""
                              style={{ ...C.select, fontSize: 11 }}>
                              <option value="">⊳ DELEGATE TO NODE...</option>
                              {childNodes.map(n => (
                                <option key={n.id} value={n.id} style={{ background: '#0a140a' }}>
                                  {n.callsign ? `${n.callsign} — ${n.name}` : n.name}
                                </option>
                              ))}
                            </select>
                            <button onClick={() => ackIntelBroadcast(intel)} style={{ ...C.btnPrimary, fontSize: 11 }}>
                              <Radio size={11} /> BROADCAST
                            </button>
                            <button onClick={() => ackIntelIgnore(intel)} style={{ ...C.btnSecondary, fontSize: 11 }}>IGNORE</button>
                          </div>
                        )}
                        {isGm && !intel.linked_bounty_id && (
                          <div className="flex gap-2 mt-3">
                            <button onClick={() => ackIntelIgnore(intel)} style={{ ...C.btnSecondary, fontSize: 11 }}>ACKNOWLEDGE</button>
                          </div>
                        )}
                      </Brackets>
                    )
                  })}
                </div>
              )}

              {/* New-schema FRAGO activated banner. Distinct from the
                  legacy intel-linked block above — this surfaces FRAGOs
                  that activated via the Phase 4 trigger types
                  (qr_discovery / *_scan / kill_event / frago_chain /
                  event_phase) and so have no intel_triggers row. Shows
                  trigger type + the compact phase strip so the GM sees
                  progression at a glance. */}
              {(() => {
                const activeStructuredFragos = visibleBounties.filter(b =>
                  b.category === 'frago'
                  && b.status === 'active'
                  && b.activation_trigger_type
                  && b.activation_trigger_type !== 'manual'
                  && (fragoStructure[b.id]?.phases || 0) > 0
                )
                if (activeStructuredFragos.length === 0) return null
                return (
                  <div className="space-y-2">
                    {activeStructuredFragos.map(f => (
                      <Brackets key={f.id} className="p-3" style={{ borderColor: '#ffcc33' }}>
                        <p style={{ ...C.mono, fontSize: 10, color: '#ffcc33', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                          ◆ FRAGO ACTIVATED — {f.mission_name || 'mission'}
                        </p>
                        <p style={{ ...C.mono, fontSize: 11, color: '#9aa6b0' }}>
                          Trigger: {(f.activation_trigger_type || '').replace(/_/g, ' ').toUpperCase()}
                          {!hideScoresForViewer && f.points ? ` · ${f.points} pts` : ''}
                        </p>
                        <div className="mt-2">
                          <FragoPhaseStepper fragoId={f.id} variant="compact" />
                        </div>
                      </Brackets>
                    ))}
                  </div>
                )
              })()}

              {/* Status counts */}
              <div className="grid grid-cols-2 gap-3">
                <Brackets className="p-3">
                  <p style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>BOUNTIES</p>
                  <p style={{ ...C.display, fontSize: 32, color: '#00ff66', lineHeight: 1, marginTop: 4 }}>
                    {nonFragoBounties.filter(b => b.status === 'active').length}
                  </p>
                  <p style={{ fontSize: 10, color: '#2a6a3a', marginTop: 4 }}>
                    {nonFragoBounties.filter(b => b.status === 'queued').length} queued · {nonFragoBounties.filter(b => b.status === 'claimed').length} claimed · {nonFragoBounties.filter(b => b.status === 'failed').length} failed
                  </p>
                </Brackets>
                <Brackets className="p-3">
                  <p style={{ ...C.mono, fontSize: 10, color: '#ffcc33', letterSpacing: '0.1em', textTransform: 'uppercase' }}>FRAGOS</p>
                  <p style={{ ...C.display, fontSize: 32, color: '#ffcc33', lineHeight: 1, marginTop: 4 }}>
                    {fragoBounties.filter(b => b.status === 'active').length}
                  </p>
                  <p style={{ fontSize: 10, color: '#2a6a3a', marginTop: 4 }}>
                    {fragoBounties.filter(b => b.status === 'queued').length} queued · {fragoBounties.filter(b => b.status === 'claimed' || b.phase === 'resolved').length} completed · {fragoBounties.filter(b => b.status === 'failed').length} failed
                  </p>
                </Brackets>
              </div>

              {/* Faction scoreboard — driven by v_player_event_stats so
                  totals reflect bounty + frago + intel awards (not just
                  the legacy session_points sum). Co-GMs see their faction
                  only; the unassigned bucket is GM-eyes-only. */}
              <Brackets className="p-3">
                <p style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>FACTION SCORES</p>
                {visibleFactions.length === 0 ? (
                  <p style={C.deep}>No factions configured</p>
                ) : (() => {
                  const scoreMap = new Map()
                  ;(factionScores || []).forEach(s => scoreMap.set(s.faction_id, s))
                  const ranked = visibleFactions
                    .map(f => ({ faction: f, score: scoreMap.get(f.id) || null }))
                    .sort((a, b) => (b.score?.total_points || 0) - (a.score?.total_points || 0))
                  const unassigned = scoreMap.get(null)
                  return (
                    <div className="space-y-2">
                      {ranked.map(({ faction: f, score }) => (
                        <div key={f.id} className="flex items-center justify-between" style={{ padding: '4px 0', borderBottom: '1px solid rgba(0,255,102,.06)' }}>
                          <div className="flex items-center gap-2">
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: f.color, display: 'inline-block' }} />
                            <span style={{ ...C.display, fontSize: 14, color: f.color }}>{f.name}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span style={{ ...C.mono, fontSize: 11, color: '#4aaa5a' }}>
                              {score?.players || 0} player{(score?.players || 0) === 1 ? '' : 's'} · {score?.kills_confirmed || 0}K / {score?.deaths || 0}D
                            </span>
                            {/* Points hidden when org enables hide_faction_scores
                                and viewer isn't a full GM. Players still see
                                player counts and K/D — the comparison is the
                                payload that gets stripped, not the activity. */}
                            {!hideScoresForViewer && (
                              <span style={{ ...C.display, fontSize: 16, color: '#ffcc33' }}>{score?.total_points || 0} PTS</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {isFullGM && unassigned && unassigned.players > 0 && (
                        <div className="flex items-center justify-between" style={{ padding: '4px 0', opacity: 0.6 }}>
                          <span style={{ ...C.display, fontSize: 14, color: '#4aaa5a' }}>UNASSIGNED</span>
                          <span style={{ ...C.mono, fontSize: 11, color: '#4aaa5a' }}>
                            {unassigned.players} player{unassigned.players === 1 ? '' : 's'}{!hideScoresForViewer ? ` · ${unassigned.total_points} pts` : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })()}
              </Brackets>

              {/* Checkpoints — cp_visibility gate. Hidden when org disables
                  CPs, when no CPs exist, or when the viewer's role doesn't
                  satisfy the visibility setting. */}
              {(() => {
                if (!orgFeatures.cp_enabled) return null
                if (!checkpoints || checkpoints.length === 0) return null
                const vis = orgFeatures.cp_visibility || 'full'
                if (vis === 'hidden' && !isFullGM) return null
                if (vis === 'limited' && !(isFullGM || isCoGm)) return null
                const visibleCps = isCoGm
                  ? checkpoints.filter(cp => {
                      const fid = cp.controlling_faction_id || cp.capturing_faction_id || null
                      return !fid || fid === coGmFactionId
                    })
                  : checkpoints
                if (visibleCps.length === 0) return null
                return (
                  <Brackets className="p-3">
                    <p style={{ ...C.mono, fontSize: 10, color: '#4dd6ff', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>CHECKPOINTS</p>
                    <div className="space-y-2">
                      {visibleCps.map(cp => {
                        const fac = cp.controlling_faction || cp.capturing_faction
                        const statusColor = cp.status === 'captured' ? '#9aff9a'
                          : cp.status === 'capturing' ? '#ffcc33'
                          : cp.status === 'contested' ? '#ff7a18'
                          : cp.status === 'locked'    ? '#5a6470'
                          : '#4dd6ff'
                        // Live countdown for active captures.
                        let countdown = null
                        if (cp.status === 'capturing' && cp.capture_started_at && cp.capture_timer_remaining_seconds != null) {
                          const elapsed = Math.floor((Date.now() - new Date(cp.capture_started_at).getTime()) / 1000)
                          countdown = Math.max(0, cp.capture_timer_remaining_seconds - elapsed)
                        }
                        // Hold duration for captured CPs.
                        let heldFor = null
                        if (cp.status === 'captured' && cp.captured_at) {
                          heldFor = Math.floor((Date.now() - new Date(cp.captured_at).getTime()) / 60000)
                        }
                        return (
                          <div key={cp.id} className="flex items-center justify-between" style={{ padding: '4px 0', borderBottom: '1px solid rgba(0,255,102,.06)' }}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: fac?.color || statusColor, display: 'inline-block', flexShrink: 0 }} />
                              <span style={{ ...C.display, fontSize: 13, color: '#e0e8de' }}>{cp.name}</span>
                              {fac && <span style={{ ...C.mono, fontSize: 10, color: fac.color }}>· {fac.name}</span>}
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              {countdown != null && (
                                <span style={{ ...C.mono, fontSize: 11, color: '#ffcc33' }}>
                                  {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2,'0')}
                                </span>
                              )}
                              {heldFor != null && (
                                <span style={{ ...C.mono, fontSize: 11, color: '#9aff9a' }}>{heldFor}m held</span>
                              )}
                              <span style={{ ...C.tag, color: statusColor, border: `1px solid ${statusColor}66`, background: statusColor + '14' }}>
                                ● {cp.status.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </Brackets>
                )
              })()}

              {/* Recent activity feed */}
              <Brackets className="p-3">
                <p style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>RECENT ACTIVITY</p>
                {recentActivity.length === 0 ? (
                  <p style={C.deep}>No activity yet</p>
                ) : (
                  <div className="space-y-1">
                    {recentActivity.map((evt, i) => (
                      <div key={i} className="flex items-baseline gap-3" style={{ fontSize: 12 }}>
                        <span style={{ ...C.mono, fontSize: 10, color: '#2a6a3a', whiteSpace: 'nowrap', minWidth: 48 }}>
                          {new Date(evt.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span style={{ ...C.mono, fontSize: 9, color: evt.kind === 'kill' ? '#ff2a2a' : '#ffcc33', minWidth: 38 }}>
                          {evt.kind === 'kill' ? 'KILL' : 'MISN'}
                        </span>
                        <span style={{ color: '#4aaa5a', flex: 1 }}>{evt.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Brackets>
            </div>
          )
        })()}

        {/* ── PLAY SHEET TAB ── */}
        {activeTab === 'play_sheet' && (
          <PlaySheet
            event={eventData}
            bounties={visibleBounties}
            factions={visibleFactions}
            mcsNodes={visibleMcsNodes}
            isGm={isFullGM}
            isCoGm={isCoGm}
            coGmFactionId={coGmFactionId}
            playSheetMode={orgFeatures.play_sheet_mode || 'limited'}
            hideScores={hideScoresForViewer}
            onRefresh={() => { fetchBounties(); fetchIntelAndDeployments() }}
          />
        )}

        {/* ── BOUNTIES TAB ── */}
        {activeTab === 'bounties' && (() => {
          const nonFrago = visibleBounties.filter(b => b.category !== 'frago')
          // Map UI labels to DB statuses. "PENDING" in the UI corresponds to
          // the DB's 'queued' value (bounties_status_check enumerates queued,
          // not pending). All other labels map 1:1.
          // Split cancelled out of the failed bucket so it gets its own
          // pill — parity with the FRAGO tab and a clearer audit trail
          // (a GM-cancelled bounty is different from one that timed out
          // or got the target killed-by-team).
          const filterMap = {
            active:    b => b.status === 'active',
            pending:   b => b.status === 'queued',
            claimed:   b => b.status === 'claimed',
            failed:    b => b.status === 'failed' || b.status === 'expired',
            cancelled: b => b.status === 'cancelled',
          }
          const filtered = nonFrago.filter(filterMap[bountyStatusFilter] || (() => true))
          const counts = {
            active:    nonFrago.filter(filterMap.active).length,
            pending:   nonFrago.filter(filterMap.pending).length,
            claimed:   nonFrago.filter(filterMap.claimed).length,
            failed:    nonFrago.filter(filterMap.failed).length,
            cancelled: nonFrago.filter(filterMap.cancelled).length,
          }
          return (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 style={{ ...C.display, fontSize: '16px' }}>BOUNTIES</h2>
                {/* Allow staging during prep so GMs can pre-load bounties before
                    GO LIVE — they're created as queued and activate on cue. */}
                {canCreateContent(eventData.status) && (
                  <button onClick={() => setShowIssueBounty(!showIssueBounty)} style={{
                    ...C.btnPrimary,
                    background: showIssueBounty ? 'rgba(255,42,42,.15)' : '#00ff66',
                    color: showIssueBounty ? '#ff2a2a' : '#000',
                    border: showIssueBounty ? '1px solid rgba(255,42,42,.3)' : 'none',
                    boxShadow: showIssueBounty ? 'none' : '0 0 6px #00ff66',
                  }}>
                    {showIssueBounty ? <><XCircle size={14} /> CANCEL</> : <><Zap size={14} /> NEW BOUNTY</>}
                  </button>
                )}
              </div>

              {/* Inline IssueBounty form. Pass the full roster — its
                  internal target filter is type-aware (kill = opposing,
                  defend = same) and uses userFactionId as the pivot. The
                  prior `visiblePlayers` pass-through pre-filtered to the
                  co-GM's faction, which collapsed to "no candidates" for
                  kill bounties (or, in dev mode, leaked to the own-faction
                  list because the dev bypass skips the type filter). */}
              {/* ANU-115: Bounty stacking toggle moved to SETTINGS tab */}

              {showIssueBounty && (
                <IssueBounty
                  inline
                  session={event}
                  players={players}
                  user={user}
                  userFactionId={isCoGm ? coGmFactionId : (players.find(p => p.user_id === user?.id)?.faction_id || null)}
                  factions={factions}
                  checkpoints={checkpoints}
                  allowBountyStacking={!!eventData.allow_bounty_stacking}
                  onClose={() => setShowIssueBounty(false)}
                  onIssued={() => { setShowIssueBounty(false); fetchBounties() }}
                />
              )}

              {/* Status filter pills */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {['active', 'pending', 'claimed', 'failed', 'cancelled'].map(s => (
                  <button key={s} onClick={() => setBountyStatusFilter(s)} style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    padding: '4px 10px',
                    background: bountyStatusFilter === s ? 'rgba(0,255,102,.2)' : 'rgba(0,20,8,.4)',
                    color: bountyStatusFilter === s ? '#00ff66' : '#4aaa5a',
                    border: bountyStatusFilter === s ? '1px solid rgba(0,255,102,.4)' : '1px solid rgba(0,255,102,.18)',
                    cursor: 'pointer',
                    boxShadow: bountyStatusFilter === s ? '0 0 4px rgba(0,255,102,.3)' : 'none',
                  }}>
                    {s} ({counts[s]})
                  </button>
                ))}
              </div>

              <BountyBoard event={eventData} bounties={filtered} onRefresh={fetchBounties} user={user} userFactionId={players.find(p => p.user_id === user?.id)?.faction_id || null} isGm={isGm} factions={visibleFactions} players={visiblePlayers} hideScores={hideScoresForViewer} isPaused={blindMan.isPaused} />
            </div>
          )
        })()}

        {/* ── FRAGOS TAB ── */}
        {activeTab === 'fragos' && (() => {
          const fragos = visibleBounties.filter(b => b.category === 'frago')
          // Map UI labels to status filters. "completed" rolls up both
          // status='claimed' and phase='resolved' since the FRAGO success
          // path sets both.
          const fragoFilterMap = {
            active:    b => b.status === 'active',
            queued:    b => b.status === 'queued',
            completed: b => b.status === 'claimed' || b.phase === 'resolved',
            failed:    b => b.status === 'failed',
            cancelled: b => b.status === 'cancelled',
          }
          const fragoCounts = {
            active:    fragos.filter(fragoFilterMap.active).length,
            queued:    fragos.filter(fragoFilterMap.queued).length,
            completed: fragos.filter(fragoFilterMap.completed).length,
            failed:    fragos.filter(fragoFilterMap.failed).length,
            cancelled: fragos.filter(fragoFilterMap.cancelled).length,
          }
          const filteredFragos = fragos.filter(fragoFilterMap[fragoStatusFilter] || (() => true))
          return (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 style={{ ...C.display, fontSize: '16px', color: '#ffcc33' }}>
                  FRAGMENTARY ORDERS ({fragoCounts.active} active)
                </h2>
                {canCreateContent(eventData.status) && isGm && (
                  // Re-fetch checkpoints + equipment on open so the
                  // builder's target dropdowns reflect anything the GM
                  // just created in the Equipment & Locations tab. The
                  // initial fetches run on mount and only refresh during
                  // the live-event 30s tick — pre-live events leave the
                  // dropdowns stale otherwise.
                  <button onClick={() => {
                    if (!showFragoForm) { fetchCheckpoints(); fetchEquipment() }
                    setShowFragoForm(!showFragoForm)
                  }} style={{
                    fontFamily: "'VT323', monospace", fontSize: '13px', letterSpacing: '0.1em',
                    textTransform: 'uppercase', background: showFragoForm ? 'rgba(255,42,42,.15)' : '#ffcc33',
                    color: showFragoForm ? '#ff2a2a' : '#000', border: showFragoForm ? '1px solid rgba(255,42,42,.3)' : 'none',
                    padding: '6px 14px', cursor: 'pointer',
                    boxShadow: showFragoForm ? 'none' : '0 0 6px #ffcc33', textShadow: 'none',
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                  }}>
                    {showFragoForm ? <><XCircle size={14} /> CANCEL</> : <><Plus size={14} /> NEW FRAGO</>}
                  </button>
                )}
              </div>

              {/* Phase 3 of the FRAGO Rework — structured three-step builder
                  (Activation Trigger / Phases & Objectives / Resolution).
                  Replaces the flat FragoForm. createFragoStructured handles
                  the multi-table write to bounties + frago_phases +
                  frago_objectives + frago_trigger_qr. */}
              {showFragoForm && (
                <FragoBuilder
                  key={editingFragoData ? editingFragoData.bounty.id : 'new'}
                  players={visiblePlayers}
                  factions={visibleFactions}
                  mcsNodes={visibleMcsNodes}
                  equipment={equipment}
                  checkpoints={checkpoints}
                  queuedBounties={visibleBounties.filter(b => b.category !== 'frago' && (b.status === 'queued' || b.status === 'awaiting_delegation'))}
                  queuedFragos={visibleBounties.filter(b => b.category === 'frago' && b.status === 'queued')}
                  submitting={fragoSubmitting}
                  onSubmit={editingFragoData ? updateFragoStructured : createFragoStructured}
                  onCancel={() => { setShowFragoForm(false); setEditingFragoData(null) }}
                  eventId={event?.id}
                  initialValues={editingFragoData ? buildFragoInitialValues(editingFragoData) : null}
                  isEditMode={!!editingFragoData}
                />
              )}

              {/* Status filter pills — same pattern as the BOUNTIES tab.
                  Phase 2 of the FRAGO Rework adds 'cancelled' so cancelled
                  missions get their own bucket instead of vanishing. */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {['active', 'queued', 'completed', 'failed', 'cancelled'].map(s => (
                  <button key={s} onClick={() => setFragoStatusFilter(s)} style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    padding: '4px 10px',
                    background: fragoStatusFilter === s ? 'rgba(255,204,51,.2)' : 'rgba(0,20,8,.4)',
                    color: fragoStatusFilter === s ? '#ffcc33' : '#4aaa5a',
                    border: fragoStatusFilter === s ? '1px solid rgba(255,204,51,.4)' : '1px solid rgba(255,204,51,.18)',
                    cursor: 'pointer',
                    boxShadow: fragoStatusFilter === s ? '0 0 4px rgba(255,204,51,.3)' : 'none',
                  }}>
                    {s} ({fragoCounts[s]})
                  </button>
                ))}
              </div>

              {/* List of FRAGOs */}
              {fragos.length === 0 ? (
                <div className="text-center py-12" style={{ color: '#4aaa5a' }}>
                  <FileText size={32} style={{ margin: '0 auto', opacity: 0.5 }} />
                  <p className="mt-3" style={{ ...C.display, fontSize: 14 }}>NO FRAGOS ISSUED</p>
                  <p style={{ ...C.mono, fontSize: 11, color: '#2a6a3a', marginTop: 4 }}>
                    {canCreateContent(eventData.status) && isGm ? 'Click + NEW FRAGO above to dispatch one.' : 'A GM hasn\'t issued any fragmentary orders yet.'}
                  </p>
                </div>
              ) : filteredFragos.length === 0 ? (
                <p className="text-center py-8" style={{ ...C.mono, fontSize: 11, color: '#2a6a3a' }}>
                  No FRAGOs match the {fragoStatusFilter} filter.
                </p>
              ) : (() => {
                // Group FRAGOs by faction so the GM gets a faction-scoped read.
                // Broadcast FRAGOs use target_faction_id directly. Delegate /
                // multi FRAGOs route through frago_assignments → mcs_nodes;
                // grab the first assigned node's faction. Anything that
                // doesn't resolve to a faction (cross-faction multi, or no
                // assignments yet) lands under ALL FACTIONS.
                const nodeFactionMap = {}
                ;(mcsNodes || []).forEach(n => { nodeFactionMap[n.id] = n.faction_id })
                const fragoFactionId = (b) => {
                  if (b.target_faction_id) return b.target_faction_id
                  const myAssigns = fragoAssignments.filter(a => a.bounty_id === b.id)
                  const factionIds = [...new Set(myAssigns.map(a => nodeFactionMap[a.node_id]).filter(Boolean))]
                  return factionIds.length === 1 ? factionIds[0] : null
                }
                const groups = new Map()
                const ungrouped = []
                filteredFragos.forEach(b => {
                  const fid = fragoFactionId(b)
                  if (fid) {
                    if (!groups.has(fid)) groups.set(fid, [])
                    groups.get(fid).push(b)
                  } else {
                    ungrouped.push(b)
                  }
                })
                const ordered = visibleFactions
                  .filter(f => groups.has(f.id))
                  .map(f => ({ faction: f, items: groups.get(f.id) }))
                // Co-GMs never see the cross-faction "ALL FACTIONS" bin —
                // those rows already failed visibility and shouldn't ghost
                // back in via the ungrouped path.
                if (ungrouped.length && isFullGM) ordered.push({ faction: null, items: ungrouped })
                return (
                  <div className="space-y-5 mt-4">
                    {ordered.map(({ faction, items }) => (
                      <div key={faction?.id || 'all'}>
                        <div className="flex items-center gap-2 mb-2 pl-1" style={{ borderLeft: `3px solid ${faction?.color || '#4aaa5a'}`, paddingLeft: 8 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: faction?.color || '#4aaa5a', display: 'inline-block' }} />
                          <span style={{ ...C.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: faction?.color || '#4aaa5a' }}>
                            {faction?.name || 'ALL FACTIONS'} — {items.length}
                          </span>
                        </div>
                        <div className="space-y-3">
                          {items.map(f => renderFragoCard(f))}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })()}
            </div>
          )
        })()}

        {/* ── EQUIPMENT & LOCATIONS TAB ── */}
        {/* Phase 2 of the FRAGO Rework — Intel tab is dissolved; what's
            left here is the catalogue of equipment + checkpoints. Intel
            triggers and scheduled deployments are absorbed into the FRAGO
            builder's activation triggers (Phase 3 / 4). */}
        {activeTab === 'gear' && (
          <EquipmentLocations
            event={eventData}
            user={user}
            isGm={isGm}
            bounties={visibleBounties}
          />
        )}

        {/* ── PLAYERS TAB ── */}
        {activeTab === 'players' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 style={{ ...C.display, fontSize: '16px' }}>EVENT ROSTER</h2>
              {(!isLocked || (isFullGM && eventData.status === 'live')) && (
                <button onClick={() => setShowAddPlayer(true)} style={C.btnPrimary}><Plus size={14} /> ADD PLAYER</button>
              )}
            </div>
            {(() => {
              // Roster view scope: full GMs see everyone (incl. pending +
              // unfactioned bins). Co-GMs see only their faction's roster.
              const rosterScope = isCoGm ? visiblePlayers : players
              const pendingPlayers = rosterScope.filter(p => p.reg_status === 'pending')
              const activePlayers = rosterScope.filter(p => p.reg_status !== 'pending')
              return (
                <div className="space-y-4">
                  {pendingPlayers.length > 0 && (
                    <Brackets bright className="overflow-hidden">
                      <button onClick={() => toggleSection('pending')} className="w-full flex justify-between items-center px-3 py-2" style={{ background: 'rgba(255,204,51,.06)', border: 'none', cursor: 'pointer', color: '#ffcc33' }}>
                        <span style={{ ...C.mono, fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>PENDING APPROVAL ({pendingPlayers.length})</span>
                        <ChevronDown size={14} style={{ color: '#ffcc33', transform: collapsedSections['pending'] ? 'rotate(-90deg)' : 'none', transition: 'transform .2s' }} />
                      </button>
                      {!collapsedSections['pending'] && (
                        <div className="space-y-1 p-2">
                          {pendingPlayers.map(player => {
                            const name = player.users?.callsign || player.users?.display_name || player.users?.email
                            const requestedFaction = factions.find(f => f.id === player.faction_id)
                            return (
                              <div key={player.user_id} className="flex items-center justify-between px-3 py-2" style={{ background: 'rgba(0,20,8,.3)' }}>
                                <div>
                                  <p className="text-sm font-medium">{name}</p>
                                  {requestedFaction && <p className="text-xs mt-0.5" style={{ color: requestedFaction.color }}>Requested: {requestedFaction.name}</p>}
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => approvePlayer(player.user_id)} style={C.btnPrimary}>APPROVE</button>
                                  <button onClick={() => removePlayer(player.user_id)} style={C.btnDanger}>DENY</button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </Brackets>
                  )}
                  {activePlayers.length === 0 && pendingPlayers.length === 0 ? (
                    <p className="text-center py-8" style={C.deep}>NO PLAYERS ADDED</p>
                  ) : activePlayers.length === 0 ? (
                    <p className="text-center py-4" style={C.deep}>NO APPROVED PLAYERS</p>
                  ) : (
                    <div className="space-y-3">
                      {/* Unassigned — full-GM only */}
                      {isFullGM && activePlayers.filter(p => !p.faction_id).length > 0 && (
                        <div>
                          <button onClick={() => toggleSection('unassigned')} className="w-full flex justify-between items-center mb-2" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4aaa5a' }}>
                            <span style={{ ...C.mono, fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>UNASSIGNED ({activePlayers.filter(p => !p.faction_id).length})</span>
                            <ChevronDown size={14} style={{ transform: collapsedSections['unassigned'] ? 'rotate(-90deg)' : 'none', transition: 'transform .2s' }} />
                          </button>
                          {!collapsedSections['unassigned'] && (
                            <div className="space-y-1">
                              {activePlayers.filter(p => !p.faction_id).map(player => (
                                <PlayerCard key={player.user_id} player={player} factions={factions} eventData={eventData} onRemove={removePlayer} onFactionChange={updatePlayerFaction} isGm={isGm} onRoleChange={updatePlayerRole} delegateScopes={delegateScopes} />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {/* By faction — visibleFactions trims this for co-GMs */}
                      {visibleFactions.map(faction => {
                        const fp = activePlayers.filter(p => p.faction_id === faction.id)
                        if (fp.length === 0) return null
                        const sk = `faction_${faction.id}`
                        return (
                          <div key={faction.id}>
                            <button onClick={() => toggleSection(sk)} className="w-full flex justify-between items-center mb-2" style={{ background: 'none', border: 'none', cursor: 'pointer', color: faction.color }}>
                              <span style={{ ...C.mono, fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: faction.color, display: 'inline-block' }} />
                                {faction.name} ({fp.length})
                              </span>
                              <ChevronDown size={14} style={{ color: faction.color, transform: collapsedSections[sk] ? 'rotate(-90deg)' : 'none', transition: 'transform .2s' }} />
                            </button>
                            {!collapsedSections[sk] && (
                              <div className="space-y-1">
                                {fp.map(player => (
                                  <PlayerCard key={player.user_id} player={player} factions={factions} eventData={eventData} onRemove={removePlayer} onFactionChange={updatePlayerFaction} isGm={isGm} onRoleChange={updatePlayerRole} delegateScopes={delegateScopes} />
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
        )}

        {/* ── EVENT QR TAB ── */}
        {activeTab === 'event_qr' && (
          <div className="flex flex-col items-center gap-6 py-4">
            <h2 style={{ ...C.display, fontSize: '16px' }}>EVENT QR CODE</h2>
            <div className="bg-white p-4"><QRCode id="event-qr-code" value={`https://death-card.vercel.app/join/${eventData.join_code}`} size={200} /></div>
            <div className="text-center">
              <p className="text-sm mb-1" style={C.dim}>Join Code</p>
              <p className="text-3xl font-bold tracking-widest" style={{ ...C.display, color: '#00ff66', textShadow: '0 0 8px rgba(0,255,102,.6)' }}>{eventData.join_code}</p>
            </div>
            <p className="text-xs" style={C.deep}>death-card.vercel.app/join/{eventData.join_code}</p>
            <div className="flex gap-3 flex-wrap justify-center">
              <button onClick={downloadEventQR} style={C.btnPrimary}><Download size={16} /> DOWNLOAD</button>
              <button onClick={printEventQR} style={C.btnSecondary}><Printer size={16} /> PRINT</button>
              {/* Full-GM only — bulk export reads every player/intel/equipment
                  QR for the event, so co-GMs (faction-scoped) shouldn't have it. */}
              {isFullGM && (
                <button onClick={() => setShowBulkQR(true)} style={C.btnSecondary}>
                  <QrCodeIcon size={14} /> BULK QR PACK
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── WAR CRIMES TAB (ANU-89) ── */}
        {activeTab === 'war_crimes' && <WarCrimesTab eventId={eventData.id} mode="gm" />}

        {/* ── MCS TAB ── */}
        {activeTab === 'mcs' && <EventMCS event={eventData} players={visiblePlayers} isGm={isGm} orgId={eventData.org_id} viewerFactionId={isCoGm ? coGmFactionId : null} />}

        {/* ── DOCTRINE TAB (ANU-201) ── */}
        {activeTab === 'doctrine' && (() => {
          const inputSt = { background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '8px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, outline: 'none', width: '100%' }
          const labelSt = { ...C.mono, fontSize: 11, color: '#4aaa5a', display: 'block', marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }
          const sectionHead = (label) => (
            <div style={{ ...C.mono, fontSize: 11, color: '#9aff9a', letterSpacing: '0.14em', textTransform: 'uppercase', borderBottom: '1px solid rgba(0,255,102,.2)', paddingBottom: 4, marginBottom: 10 }}>{label}</div>
          )
          if (!doctrineTemplates) return <p style={{ ...C.mono, fontSize: 12, color: '#4aaa5a' }}>Loading templates…</p>
          const { frago: fragoTpls, bounty: bountyTpls } = doctrineTemplates
          const tplCard = (t, type) => (
            <div key={t.id} style={{ background: 'rgba(0,20,8,.4)', border: '1px solid rgba(0,255,102,.15)', padding: '10px 12px', marginBottom: 8 }}>
              <div className="flex justify-between items-start gap-2">
                <div style={{ minWidth: 0 }}>
                  <div style={{ ...C.mono, fontSize: 13, color: '#00ff66', marginBottom: 3 }}>{t.name}</div>
                  {t.is_platform_template && (
                    <span style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", color: '#ffcc33', letterSpacing: '0.1em', background: 'rgba(255,204,51,.12)', border: '1px solid rgba(255,204,51,.3)', padding: '1px 6px', marginRight: 6 }}>PLATFORM</span>
                  )}
                  {t.description && (
                    <div style={{ fontSize: 11, color: '#9aa6b0', marginTop: 4, lineHeight: 1.4 }}>{t.description}</div>
                  )}
                  {Array.isArray(t.objectives) && t.objectives.length > 0 && (
                    <div style={{ fontSize: 10, color: '#4aaa5a', marginTop: 4 }}>{t.objectives.length} objective{t.objectives.length !== 1 ? 's' : ''}</div>
                  )}
                </div>
                <button onClick={() => {
                  setDeployModal({ template: t, type })
                  setDeployForm(type === 'frago'
                    ? { title: t.name, target_faction_id: null }
                    : { title: t.title || t.name, target_id: null, duration: t.default_duration || 0 })
                }} style={{ fontFamily: "'VT323',monospace", fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#ffcc33', color: '#000', border: 'none', padding: '5px 12px', cursor: 'pointer', flexShrink: 0, boxShadow: '0 0 4px rgba(255,204,51,.4)' }}>
                  DEPLOY
                </button>
              </div>
            </div>
          )
          return (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 style={{ ...C.display, fontSize: 16, color: '#ffcc33' }}>DOCTRINE</h2>
                <span style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', letterSpacing: '0.08em' }}>
                  {fragoTpls.length + bountyTpls.length} template{fragoTpls.length + bountyTpls.length !== 1 ? 's' : ''}
                </span>
              </div>

              {sectionHead('FRAGO TEMPLATES')}
              {fragoTpls.length === 0
                ? <p style={{ ...C.mono, fontSize: 11, color: '#4aaa5a', marginBottom: 16 }}>No FRAGO templates for this org.</p>
                : fragoTpls.map(t => tplCard(t, 'frago'))
              }

              <div style={{ marginTop: 20 }}>
                {sectionHead('BOUNTY TEMPLATES')}
                {bountyTpls.length === 0
                  ? <p style={{ ...C.mono, fontSize: 11, color: '#4aaa5a' }}>No bounty templates for this org.</p>
                  : bountyTpls.map(t => tplCard(t, 'bounty'))
                }
              </div>

              {/* ── DEPLOY MODAL ── */}
              {deployModal && (() => {
                const t = deployModal.template
                const isFrago = deployModal.type === 'frago'
                return (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,10,4,.88)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: '#061009', border: '1px solid rgba(0,255,102,.3)', padding: '20px 24px', maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                      <div style={{ ...C.display, fontSize: 15, color: '#ffcc33', marginBottom: 4 }}>DEPLOY TEMPLATE</div>
                      <div style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', letterSpacing: '0.08em', marginBottom: 16 }}>
                        {isFrago ? 'FRAGO' : 'BOUNTY'} — {t.name}
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <label style={labelSt}>{isFrago ? 'MISSION NAME' : 'BOUNTY TITLE'}</label>
                        <input style={inputSt} value={deployForm.title || ''} onChange={e => setDeployForm(f => ({ ...f, title: e.target.value }))} />
                      </div>

                      {isFrago ? (
                        <div style={{ marginBottom: 12 }}>
                          <label style={labelSt}>TARGET FACTION (blank = broadcast all)</label>
                          <select style={{ ...inputSt, padding: '8px 10px' }} value={deployForm.target_faction_id || ''} onChange={e => setDeployForm(f => ({ ...f, target_faction_id: e.target.value || null }))}>
                            <option value="">ALL FACTIONS (broadcast)</option>
                            {factions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                          </select>
                        </div>
                      ) : (
                        <>
                          <div style={{ marginBottom: 12 }}>
                            <label style={labelSt}>TARGET PLAYER *</label>
                            <select style={{ ...inputSt, padding: '8px 10px' }} value={deployForm.target_id || ''} onChange={e => setDeployForm(f => ({ ...f, target_id: e.target.value || null }))}>
                              <option value="">— select player —</option>
                              {players.map(p => (
                                <option key={p.user_id} value={p.user_id}>
                                  {p.users?.callsign || p.users?.display_name || p.user_id}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <label style={labelSt}>DURATION (minutes, 0 = unlimited)</label>
                            <input type="number" min="0" style={inputSt} value={deployForm.duration ?? 0} onChange={e => setDeployForm(f => ({ ...f, duration: parseInt(e.target.value) || 0 }))} />
                          </div>
                        </>
                      )}

                      {t.description && (
                        <div style={{ background: 'rgba(0,20,8,.3)', border: '1px solid rgba(0,255,102,.1)', padding: '8px 10px', marginBottom: 12 }}>
                          <div style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', marginBottom: 3 }}>TEMPLATE DESCRIPTION</div>
                          <div style={{ fontSize: 11, color: '#9aa6b0', lineHeight: 1.5 }}>{t.description}</div>
                        </div>
                      )}
                      {isFrago && Array.isArray(t.objectives) && t.objectives.length > 0 && (
                        <div style={{ background: 'rgba(0,20,8,.3)', border: '1px solid rgba(0,255,102,.1)', padding: '8px 10px', marginBottom: 12 }}>
                          <div style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', marginBottom: 6 }}>OBJECTIVES ({t.objectives.length})</div>
                          {t.objectives.map((o, i) => (
                            <div key={i} style={{ fontSize: 11, color: '#9aa6b0', marginBottom: 3 }}>
                              {i + 1}. {o.title || '(untitled)'} <span style={{ color: '#4aaa5a' }}>[{o.scan_type}]</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-3 justify-end" style={{ marginTop: 16 }}>
                        <button onClick={() => { setDeployModal(null); setDeployForm({}) }} style={C.btnDanger}>CANCEL</button>
                        <button
                          onClick={isFrago ? deployFragoFromTemplate : deployBountyFromTemplate}
                          disabled={deploySubmitting}
                          style={{ fontFamily: "'VT323',monospace", fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', background: deploySubmitting ? 'rgba(255,204,51,.3)' : '#ffcc33', color: '#000', border: 'none', padding: '6px 20px', cursor: deploySubmitting ? 'not-allowed' : 'pointer', opacity: deploySubmitting ? 0.7 : 1 }}
                        >
                          {deploySubmitting ? 'DEPLOYING…' : 'CONFIRM DEPLOY'}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })()}
            </div>
          )
        })()}

        {/* ── SETTINGS TAB (ANU-115) ── */}
        {activeTab === 'settings' && (() => {
          const canEdit = isFullGM
          const isConfigLocked = !isSettingsEditable(eventData)
          const durationDisplay = (() => {
            if (!eventData.starts_at || !eventData.ends_at) return null
            const ms = new Date(eventData.ends_at) - new Date(eventData.starts_at)
            if (ms <= 0) return null
            const h = Math.floor(ms / 3600000)
            const m = Math.floor((ms % 3600000) / 60000)
            return `${h}h ${m}m`
          })()
          const inputStyle = { background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.25)', padding: '8px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, outline: 'none', width: '100%' }
          const labelStyle = { ...C.mono, fontSize: 11, color: '#4aaa5a', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 4 }
          const sectionStyle = { background: 'rgba(0,20,8,.3)', border: '1px solid rgba(0,255,102,.12)', padding: '14px 16px', marginBottom: 12 }
          const sectionHeadStyle = { ...C.display, fontSize: 13, color: '#00ff66', marginBottom: 12, letterSpacing: '0.12em' }

          const timelineItems = [
            eventData.starts_at ? { label: 'STARTEX', time: eventData.starts_at, color: '#00ff66' } : null,
            ...scheduledBreaks.map(b => ({
              label: b.reason || 'BREAK', time: b.scheduled_start, endTime: b.scheduled_end, id: b.id, color: '#ffcc33',
            })),
            eventData.ends_at ? { label: 'ENDEX', time: eventData.ends_at, color: '#ff4444' } : null,
          ].filter(Boolean).sort((a, b) => new Date(a.time) - new Date(b.time))

          return (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 style={{ ...C.display, fontSize: '16px' }}>EVENT SETTINGS</h2>
                {!canEdit && <span style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', letterSpacing: '0.08em' }}>READ-ONLY — co-GM view</span>}
              </div>

              {/* ── SCHEDULE ── */}
              <div style={sectionStyle}>
                <div style={sectionHeadStyle}>SCHEDULE</div>
                {canEdit && !isConfigLocked ? (
                  settingsScheduleEditing ? (
                    <div className="flex flex-col gap-3">
                      <div>
                        <label style={labelStyle}>STARTEX</label>
                        <input type="datetime-local" value={settingsScheduleDraft.starts_at}
                          onChange={e => setSettingsScheduleDraft(d => ({ ...d, starts_at: e.target.value }))}
                          style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>ENDEX</label>
                        <input type="datetime-local" value={settingsScheduleDraft.ends_at}
                          onChange={e => setSettingsScheduleDraft(d => ({ ...d, ends_at: e.target.value }))}
                          style={inputStyle} />
                      </div>
                      {settingsScheduleDraft.starts_at && settingsScheduleDraft.ends_at && (() => {
                        const ms = new Date(settingsScheduleDraft.ends_at) - new Date(settingsScheduleDraft.starts_at)
                        if (ms <= 0) return null
                        const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000)
                        return <p style={{ ...C.mono, fontSize: 11, color: '#4aaa5a' }}>Duration: {h}h {m}m</p>
                      })()}
                      <div className="flex gap-2">
                        <button disabled={settingsScheduleSaving} onClick={async () => {
                          if (settingsScheduleDraft.starts_at && settingsScheduleDraft.ends_at && new Date(settingsScheduleDraft.ends_at) <= new Date(settingsScheduleDraft.starts_at)) { alert('ENDEX must be after STARTEX.'); return }
                          setSettingsScheduleSaving(true)
                          const ok = await saveSchedule({
                            starts_at: settingsScheduleDraft.starts_at ? new Date(settingsScheduleDraft.starts_at).toISOString() : null,
                            ends_at: settingsScheduleDraft.ends_at ? new Date(settingsScheduleDraft.ends_at).toISOString() : null,
                          })
                          setSettingsScheduleSaving(false)
                          if (ok) setSettingsScheduleEditing(false)
                        }} style={{ ...C.btnPrimary, fontSize: 12 }}>
                          {settingsScheduleSaving ? 'SAVING...' : 'SAVE'}
                        </button>
                        <button onClick={() => setSettingsScheduleEditing(false)} style={{ ...C.btnSecondary, fontSize: 12 }}>CANCEL</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <div style={{ ...C.mono, fontSize: 12, color: '#4aaa5a' }}>
                          <span style={{ color: '#00ff66' }}>STARTEX</span>{' '}
                          {fmtScheduleStamp(eventData.starts_at) || <span style={{ color: '#2a6a3a' }}>not set</span>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div style={{ ...C.mono, fontSize: 12, color: '#4aaa5a' }}>
                          <span style={{ color: '#ff4444' }}>ENDEX</span>{' '}
                          {fmtScheduleStamp(eventData.ends_at) || <span style={{ color: '#2a6a3a' }}>not set</span>}
                        </div>
                      </div>
                      {durationDisplay && <p style={{ ...C.mono, fontSize: 11, color: '#2a6a3a' }}>Duration: {durationDisplay}</p>}
                      <button onClick={() => {
                        setSettingsScheduleDraft({
                          starts_at: isoToLocalDatetimeInput(eventData.starts_at),
                          ends_at: isoToLocalDatetimeInput(eventData.ends_at),
                        })
                        setSettingsScheduleEditing(true)
                      }} style={{ ...C.btnSecondary, fontSize: 12, alignSelf: 'flex-start' }}>
                        EDIT SCHEDULE
                      </button>
                    </div>
                  )
                ) : (
                  <div className="flex flex-col gap-2">
                    <div style={{ ...C.mono, fontSize: 12, color: '#4aaa5a' }}>
                      <span style={{ color: '#00ff66' }}>STARTEX</span>{' '}
                      {fmtScheduleStamp(eventData.starts_at) || <span style={{ color: '#2a6a3a' }}>not set</span>}
                    </div>
                    <div style={{ ...C.mono, fontSize: 12, color: '#4aaa5a' }}>
                      <span style={{ color: '#ff4444' }}>ENDEX</span>{' '}
                      {fmtScheduleStamp(eventData.ends_at) || <span style={{ color: '#2a6a3a' }}>not set</span>}
                    </div>
                    {durationDisplay && <p style={{ ...C.mono, fontSize: 11, color: '#2a6a3a' }}>Duration: {durationDisplay}</p>}
                    {isConfigLocked && <p style={{ ...C.mono, fontSize: 10, color: '#2a6a3a' }}>Schedule locked — registration open</p>}
                  </div>
                )}
              </div>

              {/* ── BREAKS / PAUSES TIMELINE ── */}
              <div style={sectionStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={sectionHeadStyle}>BREAKS &amp; PAUSES</div>
                  {canEdit && !showAddBreak && (
                    <button onClick={() => setShowAddBreak(true)} style={{ ...C.btnSecondary, fontSize: 11 }}>
                      <Plus size={12} /> ADD BREAK
                    </button>
                  )}
                </div>

                {/* Timeline */}
                {timelineItems.length > 0 ? (
                  <div className="flex flex-col gap-2 mb-3">
                    {timelineItems.map((item, i) => (
                      <div key={item.id || item.label + i} className="flex items-center gap-3">
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                        <div style={{ ...C.mono, fontSize: 11, flex: 1 }}>
                          <span style={{ color: item.color, fontWeight: 600 }}>{item.label}</span>
                          <span style={{ color: '#4aaa5a', marginLeft: 8 }}>{fmtScheduleStamp(item.time)}</span>
                          {item.endTime && <span style={{ color: '#2a6a3a' }}> → {fmtScheduleStamp(item.endTime)}</span>}
                        </div>
                        {canEdit && item.id && (
                          <button onClick={() => deleteBreak(item.id)} style={{ background: 'none', border: 'none', color: '#2a6a3a', cursor: 'pointer', padding: 2 }} title="Remove break">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ ...C.mono, fontSize: 11, color: '#2a6a3a', marginBottom: 8 }}>No breaks scheduled.</p>
                )}

                {/* Add break form */}
                {canEdit && showAddBreak && (
                  <div className="flex flex-col gap-3 pt-3" style={{ borderTop: '1px solid rgba(0,255,102,.1)' }}>
                    <div>
                      <label style={labelStyle}>BREAK NAME</label>
                      <input type="text" placeholder="e.g. Lunch break" value={breakDraft.name}
                        onChange={e => setBreakDraft(d => ({ ...d, name: e.target.value }))}
                        style={inputStyle} maxLength={60} />
                    </div>
                    <div className="flex gap-2">
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>START</label>
                        <input type="datetime-local" value={breakDraft.start}
                          onChange={e => setBreakDraft(d => ({ ...d, start: e.target.value }))}
                          style={inputStyle} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>END</label>
                        <input type="datetime-local" value={breakDraft.end}
                          onChange={e => setBreakDraft(d => ({ ...d, end: e.target.value }))}
                          style={inputStyle} />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={addBreak} style={{ ...C.btnPrimary, fontSize: 12 }}>ADD</button>
                      <button onClick={() => { setShowAddBreak(false); setBreakDraft({ name: '', start: '', end: '' }) }} style={{ ...C.btnSecondary, fontSize: 12 }}>CANCEL</button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── TEAM CONFIGURATION ── */}
              <div style={sectionStyle}>
                <div style={sectionHeadStyle}>TEAM CONFIGURATION</div>
                <div className="flex items-center gap-3 mb-3">
                  <div style={{
                    padding: '4px 10px', border: `1px solid ${isConfigLocked ? 'rgba(255,100,100,.4)' : 'rgba(0,255,102,.4)'}`,
                    color: isConfigLocked ? '#ff6464' : '#00ff66',
                    ...C.mono, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
                  }}>
                    {isConfigLocked ? '🔒 LOCKED' : '🔓 UNLOCKED'}
                  </div>
                  <span style={{ ...C.mono, fontSize: 10, color: '#2a6a3a' }}>
                    {isConfigLocked ? 'Locked at registration open' : 'Editable until registration opens'}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Registration Mode', value: eventData.registration_mode || 'invite' },
                    { label: 'Command Structure', value: eventData.mcs_build_mode || 'gm_led' },
                  ].map(row => (
                    <div key={row.label} className="flex items-center justify-between" style={{ padding: '6px 0', borderBottom: '1px solid rgba(0,255,102,.06)' }}>
                      <span style={{ ...C.mono, fontSize: 11, color: '#4aaa5a' }}>{row.label}</span>
                      <span style={{ ...C.mono, fontSize: 11, color: '#00ff66', textTransform: 'uppercase' }}>{row.value.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── GENERAL SETTINGS ── */}
              {isFullGM && (
                <div style={sectionStyle}>
                  <div style={sectionHeadStyle}>GENERAL</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span style={{ ...C.mono, fontSize: 11, color: '#4aaa5a', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Allow Bounty Stacking</span>
                      <p style={{ ...C.mono, fontSize: 10, color: '#2a6a3a', marginTop: 2 }}>Stack points on duplicate bounties instead of blocking</p>
                    </div>
                    <div onClick={async () => {
                      const next = !eventData.allow_bounty_stacking
                      await supabase.from('events').update({ allow_bounty_stacking: next }).eq('id', eventData.id)
                      setEventData(prev => ({ ...prev, allow_bounty_stacking: next }))
                    }} style={{ position: 'relative', width: 40, height: 20, borderRadius: 10, background: eventData.allow_bounty_stacking ? '#00ff66' : 'rgba(0,255,102,.15)', border: '1px solid rgba(0,255,102,.3)', cursor: 'pointer', flexShrink: 0 }}>
                      <div style={{ position: 'absolute', top: 2, left: eventData.allow_bounty_stacking ? 20 : 2, width: 14, height: 14, borderRadius: '50%', background: eventData.allow_bounty_stacking ? '#000' : '#4aaa5a', transition: 'left .2s' }} />
                    </div>
                  </div>

                  {/* ANU-132 — Field intel pre-op toggle */}
                  <div className="flex items-center justify-between" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(0,255,102,.08)' }}>
                    <div>
                      <span style={{ ...C.mono, fontSize: 11, color: !orgFieldIntelEnabled ? '#2a5a3a' : '#4aaa5a', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Field Intel Posts</span>
                      <p style={{ ...C.mono, fontSize: 10, color: '#2a6a3a', marginTop: 2 }}>
                        {!orgFieldIntelEnabled ? 'Disabled at org level — contact org admin' : 'Allow players to post field intel updates to bounty/FRAGO dossiers'}
                      </p>
                    </div>
                    <div
                      onClick={async () => {
                        if (!orgFieldIntelEnabled) return
                        const next = !eventData.field_intel_enabled
                        await supabase.from('events').update({ field_intel_enabled: next }).eq('id', eventData.id)
                        setEventData(prev => ({ ...prev, field_intel_enabled: next }))
                      }}
                      style={{ position: 'relative', width: 40, height: 20, borderRadius: 10, background: !orgFieldIntelEnabled ? 'rgba(0,255,102,.05)' : eventData.field_intel_enabled ? '#00ff66' : 'rgba(0,255,102,.15)', border: '1px solid rgba(0,255,102,.3)', cursor: !orgFieldIntelEnabled ? 'not-allowed' : 'pointer', flexShrink: 0, opacity: !orgFieldIntelEnabled ? 0.4 : 1 }}>
                      <div style={{ position: 'absolute', top: 2, left: eventData.field_intel_enabled && orgFieldIntelEnabled ? 20 : 2, width: 14, height: 14, borderRadius: '50%', background: eventData.field_intel_enabled && orgFieldIntelEnabled ? '#000' : '#4aaa5a', transition: 'left .2s' }} />
                    </div>
                  </div>
                </div>
              )}

              {/* ── CANCEL EVENT ── */}
              {isFullGM && ['draft','prep','registration','locked'].includes(eventData.status) && (
                <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid rgba(255,42,42,.2)' }}>
                  <button
                    onClick={() => transitionStatus('cancelled', { destructive: true, confirmMessage: 'This will cancel the event and notify all registered players. This cannot be undone.' })}
                    style={{ ...C.btnStatus('rgba(255,42,42,.15)', '#ff2a2a'), width: '100%', justifyContent: 'center', padding: '10px 14px' }}
                  >
                    <XCircle size={14} /> CANCEL EVENT
                  </button>
                </div>
              )}
            </div>
          )
        })()}
      </main>

      {/* IssueBounty is rendered INLINE inside the BOUNTIES tab now —
          the old modal trigger here would have rendered a duplicate. */}
      {showAddPlayer && (
        <AddPlayer
          session={event}
          onClose={() => setShowAddPlayer(false)}
          onAdded={() => { setShowAddPlayer(false); fetchPlayers() }}
          factions={eventData.status === 'live' ? factions : []}
        />
      )}
    </div>
  )

  function PlayerCard({ player, factions, eventData, onRemove, onFactionChange, isGm, onRoleChange, delegateScopes }) {
    const faction = factions.find(f => f.id === player.faction_id)
    const isGmRole = player.event_role === 'gm'
    const rt = roleTag[player.event_role]
    // ANU-84 — flex-wrap on the row + the right-side cluster so action
    // controls drop to a second line on narrow mobile viewports instead
    // of crushing the name cell. min-w-0 + truncate keeps the name
    // ellipsis-clipped when the row is still tight.
    return (
      <div className="flex justify-between items-center gap-2 px-3 py-2 flex-wrap" style={{ background: 'rgba(0,20,8,.3)', border: '1px solid rgba(0,255,102,.08)' }}>
        <div className="min-w-0 flex-1" style={{ minWidth: '8rem' }}>
          <p className="text-sm font-medium truncate" style={{ ...C.display, fontSize: '13px', overflowWrap: 'anywhere' }}>{player.users?.callsign || player.users?.display_name || player.users?.email}</p>
          <p className="text-xs truncate" style={{ ...C.dim, overflowWrap: 'anywhere' }}>{player.users?.display_name || player.users?.email}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          {/* Role tag — co-GMs read as the faction they GM, not the generic
              CO_GM tag. Legacy delegate rows still render read-only. */}
          {rt && (
            <span style={{ ...C.tag, ...rt }}>
              {player.event_role === 'co_gm' && player.co_gm_faction_id
                ? `${factions.find(f => f.id === player.co_gm_faction_id)?.name?.toUpperCase() || 'CO-GM'} · CO-GM`
                : player.event_role === 'delegate'
                ? (delegateScopes.find(s => s.name === player.delegate_scope)?.name || player.delegate_scope || 'Delegate')
                : player.event_role.toUpperCase()}
            </span>
          )}
          {/* GM Access dropdown — three states encoded in a single value:
                'player'       → demote to player
                'gm'           → full GM
                'co_gm:<fid>'  → co-GM scoped to faction <fid>
              co-GM with no scope is meaningless, so the picker collapses
              the role+faction into one onChange. */}
          {isGm && !isGmRole && (
            <select
              value={
                player.event_role === 'gm' ? 'gm'
                : player.event_role === 'co_gm' && player.co_gm_faction_id ? `co_gm:${player.co_gm_faction_id}`
                : player.event_role === 'co_gm' ? 'co_gm'
                : player.event_role === 'iga' ? 'iga'
                : 'player'
              }
              onChange={(e) => {
                const v = e.target.value
                if (v === 'gm') return onRoleChange(player.user_id, 'gm', null)
                if (v === 'player') return onRoleChange(player.user_id, 'player', null)
                if (v === 'iga') return onRoleChange(player.user_id, 'iga', null)
                if (v.startsWith('co_gm:')) return onRoleChange(player.user_id, 'co_gm', v.slice('co_gm:'.length))
                if (v === 'co_gm') return onRoleChange(player.user_id, 'co_gm', null)
              }}
              style={C.select}
              title="GM Access"
            >
              <option value="player">Player</option>
              <option value="gm">Full GM</option>
              {/* ANU-90 — IGA: referee tier. Sees both factions read-only,
                  can fire BLIND MAN, ack war crimes. No event writes. */}
              <option value="iga">In-Game Admin (IGA)</option>
              {factions.map(f => (
                <option key={f.id} value={`co_gm:${f.id}`}>Faction GM: {f.name}</option>
              ))}
            </select>
          )}
          {factions.length > 0 && (
            <select value={player.faction_id || ''} onChange={(e) => onFactionChange(player.user_id, e.target.value || null)} style={{ ...C.select, color: faction ? faction.color : '#4aaa5a', borderColor: faction ? faction.color + '66' : 'rgba(0,255,102,.3)' }}>
              <option value="" style={{ background: '#0a140a', color: '#4aaa5a' }}>Unassigned</option>
              {factions.map(f => <option key={f.id} value={f.id} style={{ background: '#0a140a', color: '#00ff66' }}>{f.name}</option>)}
            </select>
          )}
          <span style={{ ...C.display, fontSize: '13px', color: '#ffcc33' }}>{player.session_points} pts</span>
          {eventData.status !== 'closed' && (
            <button onClick={() => onRemove(player.user_id)} style={{ background: 'none', border: 'none', color: '#2a6a3a', cursor: 'pointer' }}><XCircle size={16} /></button>
          )}
        </div>
      </div>
    )
  }

  // FRAGO list-card component. Shows mission, status, assignments, and the
  // GM action buttons (Success / Fail / Reassign / Resend) gated on status.
  // ANU-112: same fix as ANU-36 in BountyBoard — function defined inside
  // GameEvent body creates a new component type on every parent re-render,
  // causing React to unmount/remount the subtree and drop input focus.
  // Converted to a plain render function so element types stay stable.
  function renderFragoCard(bounty) {
    const myAssignments = fragoAssignments.filter(a => a.bounty_id === bounty.id)
    const targetFaction = bounty.target_type === 'faction' ? factions.find(fac => fac.id === bounty.target_faction_id) : null
    const isExpanded = !!expandedFrago[bounty.id]
    const isEditing = editingFragoId === bounty.id
    const canEditContent = isGm && isContentEditable(eventData?.status)
    const startEditFrago = () => {
      setEditingFragoId(bounty.id)
      setFragoDraft({
        mission_name: bounty.mission_name || '',
        points: bounty.points ?? '',
        notes: bounty.notes || '',
      })
      setExpandedFrago(e => ({ ...e, [bounty.id]: true }))
    }
    const saveEditFrago = async () => {
      const ptsNum = fragoDraft.points === '' ? null : parseInt(fragoDraft.points)
      const { error } = await supabase.from('bounties').update({
        mission_name: fragoDraft.mission_name.trim() || null,
        points: ptsNum,
        notes: fragoDraft.notes.trim() || null,
      }).eq('id', bounty.id)
      if (error) { alert('Save failed: ' + error.message); return }
      setEditingFragoId(null)
      fetchBounties()
    }
    const statusColor = bounty.status === 'active' ? '#ffcc33'
      : bounty.status === 'queued' ? '#4da0ff'
      : bounty.status === 'failed' ? '#ff2a2a'
      : bounty.status === 'cancelled' ? '#9aa6b0'
      : bounty.status === 'claimed' ? '#9aff9a'
      : '#4aaa5a'
    const statusLabel = bounty.status === 'claimed' ? 'RESOLVED' : bounty.status.toUpperCase()
    const expiresAt = bounty.expires_at ? new Date(bounty.expires_at) : null
    // FRAGO Rework Phase 2: surface the new schema on the card so the GM
    // can see the activation trigger + how big the FRAGO is at a glance
    // without opening the detail view (Phase 5 builds the full stepper).
    const struct = fragoStructure[bounty.id] || { phases: 0, objectives: 0 }
    const triggerLabel = bounty.activation_trigger_type
      ? bounty.activation_trigger_type.replace(/_/g, ' ').toUpperCase()
      : null

    return (
      <div key={bounty.id} className="p-4" style={{
        background: 'rgba(255,204,51,.04)',
        border: `1px solid ${bounty.status === 'active' ? 'rgba(255,204,51,.4)' : 'rgba(255,204,51,.15)'}`,
        boxShadow: bounty.status === 'active' ? '0 0 6px rgba(255,204,51,.1)' : 'none',
        position: 'relative',
        opacity: bounty.status === 'claimed' ? 0.65 : 1,
      }}>
        <div style={{ position:'absolute',top:-1,left:-1,width:6,height:6,borderTop:`2px solid ${statusColor}`,borderLeft:`2px solid ${statusColor}` }}/>
        <div style={{ position:'absolute',bottom:-1,right:-1,width:6,height:6,borderBottom:`2px solid ${statusColor}`,borderRight:`2px solid ${statusColor}` }}/>

        {/* Header row */}
        <div className="flex justify-between items-start gap-3 mb-2">
          <button onClick={() => setExpandedFrago(e => ({ ...e, [bounty.id]: !isExpanded }))}
            className="min-w-0 flex-1 text-left"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}>
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronDown size={12} style={{ color: '#ffcc33' }} /> : <ChevronRightIcon size={12} style={{ color: '#ffcc33' }} />}
              <p style={{ ...C.display, fontSize: 16, color: '#ffcc33', textShadow: '0 0 4px rgba(255,204,51,.3)', margin: 0 }}>
                {bounty.mission_name || '(no mission name)'}
              </p>
            </div>
            <p style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', marginTop: 2 }}>
              {/* UX cleanup — FRAGO headline points read from
                  resolution_on_success.points (the source of truth in the
                  new builder), falling back to bounty.points for legacy
                  rows created before the top-level field was removed. */}
              {!hideScoresForViewer && <>{bounty.resolution_on_success?.points ?? bounty.points ?? 0} PTS · </>}{bounty.target_type === 'faction'
                ? (() => {
                    const pursuingCount = myAssignments.filter(a => a.status === 'pursuing').length
                    return `BROADCAST → ${targetFaction?.name || 'faction'}${pursuingCount > 0 ? ` · ${pursuingCount} pursuing` : ''}`
                  })()
                : myAssignments.length > 0
                  ? `${myAssignments.filter(a => a.status === 'active').length} active assignment${myAssignments.filter(a => a.status === 'active').length === 1 ? '' : 's'}`
                  : 'no assignments'}
              {expiresAt && bounty.status === 'active' && (() => {
                const msLeft = expiresAt - Date.now()
                const minLeft = Math.ceil(msLeft / 60000)
                return msLeft > 0 ? ` · expires in ${minLeft}m` : ' · expired'
              })()}
            </p>
            {/* Phase 2: trigger type + phase/objective summary from the
                new schema. Hidden for FRAGOs that pre-date the rework. */}
            {(triggerLabel || struct.phases > 0) && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {triggerLabel && (
                  <span style={{
                    ...C.mono, fontSize: 9, letterSpacing: '0.08em',
                    padding: '1px 6px',
                    background: 'rgba(77,160,255,.08)',
                    border: '1px solid rgba(77,160,255,.3)',
                    color: '#4da0ff',
                  }}>
                    ◆ {triggerLabel}
                  </span>
                )}
                {struct.phases > 0 && (
                  <span style={{
                    ...C.mono, fontSize: 9, letterSpacing: '0.08em',
                    padding: '1px 6px',
                    background: 'rgba(255,204,51,.06)',
                    border: '1px solid rgba(255,204,51,.25)',
                    color: '#ffcc33',
                  }}>
                    {struct.phases} PHASE{struct.phases === 1 ? '' : 'S'} · {struct.objectives} OBJ
                  </span>
                )}
              </div>
            )}
          </button>
          <span style={{
            ...C.tag,
            background: statusColor + '14',
            border: `1px solid ${statusColor}66`,
            color: statusColor,
          }}>
            ● {statusLabel}
          </span>
        </div>

        {/* Briefing */}
        {bounty.notes && (
          <p style={{ ...C.mono, fontSize: 11, color: '#4aaa5a', marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {bounty.notes}
          </p>
        )}

        {/* Assignments list (delegate/multi/pursuing) */}
        {myAssignments.length > 0 && (
          <div className="mt-3" style={{ borderTop: '1px solid rgba(255,204,51,.15)', paddingTop: 8 }}>
            <p style={{ ...C.mono, fontSize: 9, color: '#2a6a3a', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
              {bounty.target_type === 'faction'
                ? `PURSUING (${myAssignments.filter(a => a.status === 'pursuing').length})`
                : 'ASSIGNMENTS'}
            </p>
            <div className="space-y-1">
              {myAssignments.map(a => {
                const node = a.node || mcsNodes.find(n => n.id === a.node_id)
                const nodeFaction = node ? factions.find(fac => fac.id === node.faction_id) : null
                const teamName = a.node?.team?.name
                const statusColor =
                  a.status === 'active' ? '#ffcc33' :
                  a.status === 'accepted' ? '#9aff9a' :
                  a.status === 'completed' ? '#9aff9a' :
                  a.status === 'failed' ? '#ff2a2a' :
                  a.status === 'pursuing' ? '#4da0ff' :
                  '#2a6a3a'
                return (
                  <div key={a.id} className="flex items-center justify-between gap-2" style={{ fontSize: 11 }}>
                    <span style={{ color: '#4aaa5a' }}>
                      {node?.callsign ? `${node.callsign} — ${node.name}` : node?.name || a.node_id.slice(0, 8)}
                      {teamName && <span style={{ color: '#9aff9a', marginLeft: 6 }}>· ◇ {teamName}</span>}
                      {nodeFaction && <span style={{ color: nodeFaction.color, marginLeft: 6 }}>· {nodeFaction.name}</span>}
                    </span>
                    <span style={{
                      ...C.mono, fontSize: 9, letterSpacing: '0.1em',
                      color: statusColor,
                    }}>
                      {a.status.toUpperCase()}
                    </span>
                    {/* Promote a pursuing node to a formal active delegation
                        (broadcast → delegated). GM-only, FRAGO must be live. */}
                    {a.status === 'pursuing' && isGm && bounty.status === 'active' && (
                      <button onClick={() => promotePursuit(a)} style={{
                        fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                        background: 'rgba(0,255,102,.12)', color: '#00ff66',
                        border: '1px solid rgba(0,255,102,.4)',
                        padding: '2px 6px', cursor: 'pointer',
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                      }}>
                        ✓ DELEGATE
                      </button>
                    )}
                    {/* Reassign — active assignments only, GM only.
                        Co-GMs only see nodes in their own faction (Bug 30).
                        visibleMcsNodes is already scoped upstream. */}
                    {a.status === 'active' && isGm && bounty.status === 'active' && (
                      <select onChange={e => reassignFrago(a, e.target.value)} value="" style={{ ...C.select, fontSize: 10, padding: '2px 6px' }}>
                        <option value="">↻ reassign...</option>
                        {visibleMcsNodes.filter(n => n.id !== a.node_id).map(n => (
                          <option key={n.id} value={n.id} style={{ background: '#0a140a' }}>
                            → {n.callsign ? `${n.callsign} — ${n.name}` : n.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Action buttons */}
        {isGm && (
          <div className="flex gap-2 mt-3" style={{ borderTop: '1px solid rgba(255,204,51,.15)', paddingTop: 8 }}>
            {/* ANU-81 — FRAGOs spawned as resolution items of a parent FRAGO
                are locked from manual transitions; parent owns lifecycle. */}
            {bounty.parent_frago_id && (bounty.status === 'queued' || bounty.status === 'active') && (
              <span style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#9aa6b0', padding: '6px 12px',
                border: '1px dashed rgba(154,166,176,.4)',
              }} title="Lifecycle owned by parent FRAGO — manual transitions disabled.">
                ⛓ LINKED — PARENT FRAGO OWNS LIFECYCLE
              </span>
            )}
            {bounty.status === 'queued' && !bounty.parent_frago_id && (() => {
              // ANU-207 — doctrine-deployed FRAGOs must be reviewed before activation.
              // source_frago_template_id set = deployed from doctrine; doctrine_reviewed
              // flips to true when the GM EDIT+saves via updateFragoStructured.
              const isDoctrine = !!bounty.source_frago_template_id
              const needsReview = isDoctrine && !bounty.doctrine_reviewed
              return (
                <>
                  <button onClick={() => startFullFragoEdit(bounty)} style={{
                    fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.1em',
                    textTransform: 'uppercase', padding: '6px 12px', cursor: 'pointer',
                    background: needsReview ? 'rgba(255,140,0,.2)' : 'rgba(255,204,51,.12)',
                    color: needsReview ? '#ff9a3d' : '#ffcc33',
                    border: needsReview ? '1px solid rgba(255,140,0,.6)' : '1px solid rgba(255,204,51,.4)',
                    boxShadow: needsReview ? '0 0 6px rgba(255,140,0,.25)' : 'none',
                  }}>
                    {needsReview ? '⚠ REVIEW' : '✎ EDIT'}
                  </button>
                  <button
                    onClick={() => activateFrago(bounty)}
                    disabled={needsReview}
                    title={needsReview ? 'Review this FRAGO before activating' : undefined}
                    style={{
                      fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.1em',
                      textTransform: 'uppercase', padding: '6px 12px', cursor: needsReview ? 'not-allowed' : 'pointer',
                      background: needsReview ? 'rgba(77,160,255,.06)' : 'rgba(77,160,255,.15)',
                      color: needsReview ? '#2a5a8a' : '#4da0ff',
                      border: needsReview ? '1px solid rgba(77,160,255,.2)' : '1px solid rgba(77,160,255,.4)',
                      textShadow: needsReview ? 'none' : '0 0 4px rgba(77,160,255,.3)',
                      opacity: needsReview ? 0.5 : 1,
                    }}>
                    ▶ ACTIVATE
                  </button>
                </>
              )
            })()}
            {bounty.status === 'active' && !bounty.parent_frago_id && (
              <>
                <button onClick={() => completeFrago(bounty)} style={{
                  fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.1em',
                  textTransform: 'uppercase', background: 'rgba(0,255,102,.15)', color: '#00ff66',
                  border: '1px solid rgba(0,255,102,.4)', padding: '6px 12px', cursor: 'pointer',
                  textShadow: '0 0 4px rgba(0,255,102,.3)',
                }}>
                  ✅ MISSION SUCCESS
                </button>
                <button onClick={() => failFrago(bounty)} style={{
                  fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.1em',
                  textTransform: 'uppercase', background: 'rgba(255,42,42,.15)', color: '#ff2a2a',
                  border: '1px solid rgba(255,42,42,.4)', padding: '6px 12px', cursor: 'pointer',
                  textShadow: '0 0 4px rgba(255,42,42,.3)',
                }}>
                  ❌ MISSION FAILED
                </button>
                <button onClick={() => cancelFrago(bounty)} style={{
                  fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.1em',
                  textTransform: 'uppercase', background: 'rgba(154,166,176,.12)', color: '#9aa6b0',
                  border: '1px solid rgba(154,166,176,.4)', padding: '6px 12px', cursor: 'pointer',
                }}>
                  ⊘ CANCEL
                </button>
              </>
            )}
            {bounty.status === 'queued' && !bounty.parent_frago_id && (
              <button onClick={() => cancelFrago(bounty)} style={{
                fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.1em',
                textTransform: 'uppercase', background: 'rgba(154,166,176,.12)', color: '#9aa6b0',
                border: '1px solid rgba(154,166,176,.4)', padding: '6px 12px', cursor: 'pointer',
              }}>
                ⊘ CANCEL
              </button>
            )}
            {bounty.status === 'failed' && (
              <button onClick={() => resendFrago(bounty)} style={{
                fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.1em',
                textTransform: 'uppercase', background: 'rgba(255,204,51,.15)', color: '#ffcc33',
                border: '1px solid rgba(255,204,51,.4)', padding: '6px 12px', cursor: 'pointer',
                textShadow: '0 0 4px rgba(255,204,51,.3)',
              }}>
                ↻ RESEND
              </button>
            )}
          </div>
        )}

        {isExpanded && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,204,51,.15)' }}>
            {isEditing ? (
              <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ ...C.mono, fontSize: 10, color: '#9aff9a', display: 'block', marginBottom: 2 }}>MISSION NAME</label>
                  <input value={fragoDraft.mission_name} onChange={e => setFragoDraft(d => ({ ...d, mission_name: e.target.value }))} maxLength={120}
                    style={{ width: '100%', background: 'rgba(0,20,8,.6)', color: '#ffcc33', border: '1px solid rgba(255,204,51,.3)', padding: '6px 10px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", outline: 'none' }} />
                </div>
                <div>
                  <label style={{ ...C.mono, fontSize: 10, color: '#9aff9a', display: 'block', marginBottom: 2 }}>POINTS</label>
                  <input type="number" min={0} max={10000} value={fragoDraft.points} onChange={e => setFragoDraft(d => ({ ...d, points: e.target.value }))}
                    style={{ width: '100%', background: 'rgba(0,20,8,.6)', color: '#ffcc33', border: '1px solid rgba(255,204,51,.3)', padding: '6px 10px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", outline: 'none' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ ...C.mono, fontSize: 10, color: '#9aff9a', display: 'block', marginBottom: 2 }}>BRIEFING / NOTES</label>
                  <textarea value={fragoDraft.notes} onChange={e => setFragoDraft(d => ({ ...d, notes: e.target.value }))} rows={3} maxLength={1000}
                    style={{ width: '100%', background: 'rgba(0,20,8,.6)', color: '#ffcc33', border: '1px solid rgba(255,204,51,.3)', padding: '6px 10px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", outline: 'none', resize: 'vertical' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }} className="flex gap-2">
                  <button onClick={saveEditFrago} className="flex items-center gap-1 px-3 py-1"
                    style={{ background: '#ffcc33', color: '#000', border: 'none', cursor: 'pointer', fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}><Check size={12} /> SAVE</button>
                  <button onClick={() => setEditingFragoId(null)} className="flex items-center gap-1 px-3 py-1"
                    style={{ background: 'rgba(0,20,8,.4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', cursor: 'pointer', ...C.mono, fontSize: 11 }}><XIcon size={11} /> CANCEL</button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {bounty.activation_message && (
                  <p style={{ ...C.mono, fontSize: 11, color: '#4dd6ff', fontStyle: 'italic' }}><span style={{ color: '#4aaa5a' }}>ACTIVATION:</span> "{bounty.activation_message}"</p>
                )}
                {bounty.target_type && (
                  <p style={{ ...C.mono, fontSize: 11, color: '#9aa6b0' }}><span style={{ color: '#4aaa5a' }}>TARGET TYPE:</span> {bounty.target_type}</p>
                )}
                {expiresAt && (
                  <p style={{ ...C.mono, fontSize: 11, color: '#9aa6b0' }}><span style={{ color: '#4aaa5a' }}>EXPIRES:</span> {expiresAt.toLocaleString()}</p>
                )}
                <p style={{ ...C.mono, fontSize: 11, color: '#9aa6b0' }}>
                  <span style={{ color: '#4aaa5a' }}>STATUS:</span> {bounty.status} · <span style={{ color: '#4aaa5a' }}>PHASE:</span> {bounty.phase || '—'}
                </p>
                <div className="flex gap-2 mt-2">
                  {canEditContent && (
                    <button onClick={startEditFrago} className="flex items-center gap-1 px-2 py-1"
                      style={{ background: 'rgba(0,20,8,.4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', cursor: 'pointer', ...C.mono, fontSize: 11 }}><Pencil size={11} /> EDIT</button>
                  )}
                  <a href={`/frago/${bounty.id}`} className="flex items-center gap-1 px-2 py-1"
                    style={{ background: 'rgba(0,20,8,.4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', cursor: 'pointer', ...C.mono, fontSize: 11, textDecoration: 'none' }}>DOSSIER →</a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
}
